// Data layer (live) — คุยกับ inbound ผ่าน supabase client
// รองรับ 2 โหมด: native (schema inbound) และ PUBLIC BRIDGE (public.ib_* — ระหว่าง
// รอ inbound expose). ใช้ T() map ชื่อ + client-side join (ไม่พึ่ง PostgREST embed
// ซึ่งไม่ทำงานผ่าน view ใน bridge). MOCK จัดการที่ระดับ page.
import { supabase, BRIDGE, T } from './supabase.js';
import { toUploadableImage } from './image.js';
import { itemName, itemNameTh, hasThai } from './naming.js';
import { thaiLayoutToEn } from './kbd.js';
import { hasMaterial, safeResearchRow, variantsDiffer } from './materials.js';

const err = (e) => { if (e) throw e; };
const tbl = (name) => supabase.from(T(name));
const rpcName = (name) => (BRIDGE ? `ib_${name}` : name);
const rows = async (q) => { const { data, error } = await q; err(error); return data ?? []; };
const idx = (arr, key = 'id') => Object.fromEntries(arr.map(r => [r[key], r]));

// ── shipments / console ──────────────────────────────────────────────────────
export async function listShipments() {
  const ships = await rows(tbl('shipment')
    .select('*').neq('status', 'void')
    .order('eta', { ascending: true, nullsFirst: false }));
  const ids = ships.map(s => s.id);
  if (!ids.length) return ships;
  const [cons, reqs] = await Promise.all([
    rows(tbl('consignment')
      // house_bl ใส่ไว้ให้ช่องค้นหาของ Console หาเลข B/L ของ supplier เจอด้วย
      // ⚠ ต้องมีทุกช่องที่หน้าต่าง "แก้ข้อมูล supplier" แก้ได้ — เดิมขาด invoice_date: เปิดหน้าต่างทีไรวันที่ว่าง
      //   (Safari โชว์วันนี้สีเทาแทน ดูเหมือนวันที่ถูกเปลี่ยน) และกดบันทึกโดยไม่พิมพ์ใหม่ = ลบวันที่ทิ้ง (user แจ้ง 27 ต.ค. 69)
      .select('id, shipment_id, supplier_name, origin_country, invoice_no, invoice_date, house_bl, incoterm, currency')
      .in('shipment_id', ids)),
    rows(tbl('document_requirement')
      .select('id, shipment_id, consignment_id, req_type, side, needed_by, remind_before, required, status, conditional_reason, matched_document_id, updated_at')
      .in('shipment_id', ids)),
  ]);
  // ช่องที่ status = 'na' = ตัดสินแล้วว่า "ไม่ต้องใช้" (ตอนนี้ใช้กับ CO = ไม่ใช้สิทธิ์ FTA · setFtaWaived)
  //   ทำให้ required = false ตรงนี้ที่เดียว → ทุกหน้า (Console · วันนี้ · แบรนด์ · ด่าน Form) นับว่าไม่ต้องใช้เหมือนกันหมด
  //   ⚠ ต้องแก้ตรงนี้ ไม่ใช่ใน DB: compute_requirements เขียน required ของ CO ทับทุกครั้งที่รัน
  //     (เพิ่มบรรทัด/แก้พิกัด/นำเข้า invoice) แต่ไม่แตะ status — status จึงเป็นที่เดียวที่การตัดสินนี้อยู่รอด
  for (const r of reqs) if (r.status === 'na') { r.waived = true; r.required = false; }
  for (const s of ships) {
    s.consignment = cons.filter(c => c.shipment_id === s.id);
    s.document_requirement = reqs.filter(r => r.shipment_id === s.id);
  }
  return ships;
}

export async function getDocuments(shipmentId) {
  return rows(tbl('document').select('*').eq('shipment_id', shipmentId)
    .order('uploaded_at', { ascending: false }));
}

export async function getCertificates(consignmentIds) {
  if (!consignmentIds.length) return [];
  return rows(tbl('certificate_of_origin').select('*').in('consignment_id', consignmentIds));
}

// แท็บ "วันนี้": ตู้ทั้งหมด (ไม่ void) + CO ของทุก consignment — คำนวณงานประจำวันฝั่ง client
export async function loadTodayBoard() {
  const ships = await listShipments();
  const consIds = ships.flatMap(s => (s.consignment ?? []).map(c => c.id));
  const certs = consIds.length ? await getCertificates(consIds) : [];
  return { ships, certs };
}

// ── งานของฉัน / เตือนความจำ (to-do + reminder + ปฏิทิน) — ทีมเห็นร่วมกัน ──
export async function listReminders() {
  return rows(tbl('reminder').select('*')
    .order('done', { ascending: true })
    .order('due_date', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: false }));
}
// ผูกได้ทั้งตู้ (shipmentId) และแบรนด์ (partnerId) + บันทึก/ประชุมของแบรนด์ (noteId) — deploy-23
export async function addReminder({ title, note, dueDate, dueTime, shipmentId, partnerId, noteId, assignee }) {
  const t = (title || '').trim();
  if (!t) return null;
  const { data, error } = await tbl('reminder').insert({
    title: t, note: (note || '').trim() || null,
    due_date: dueDate || null, due_time: (dueTime || '').trim() || null,
    shipment_id: shipmentId || null, partner_id: partnerId || null, note_id: noteId || null,
    assignee: assignee || null,   // มอบหมายให้ใคร (deploy-24) — null = ทั้งทีม
  }).select().single();
  err(error);
  return data;
}
export async function updateReminder(id, { title, note, dueDate, dueTime, shipmentId, partnerId, noteId, assignee }) {
  const patch = { updated_at: new Date().toISOString() };
  if (title !== undefined) patch.title = (title || '').trim();
  if (note !== undefined) patch.note = (note || '').trim() || null;
  if (dueDate !== undefined) patch.due_date = dueDate || null;
  if (dueTime !== undefined) patch.due_time = (dueTime || '').trim() || null;
  if (shipmentId !== undefined) patch.shipment_id = shipmentId || null;
  if (partnerId !== undefined) patch.partner_id = partnerId || null;
  if (noteId !== undefined) patch.note_id = noteId || null;
  if (assignee !== undefined) patch.assignee = assignee || null;
  const { error } = await tbl('reminder').update(patch).eq('id', id);
  err(error);
}
export async function setReminderDone(id, done) {
  const { error } = await tbl('reminder').update({
    done, done_at: done ? new Date().toISOString() : null, updated_at: new Date().toISOString(),
  }).eq('id', id);
  err(error);
}
export async function deleteReminder(id) {
  const { error } = await tbl('reminder').delete().eq('id', id);
  err(error);
}
// รายชื่อทีมของแอพนี้ (แถวใน app_role) ไว้มอบหมายงาน — [{user_id, name, role, is_active}]
export async function listStaff() {
  return rows(tbl('v_staff').select('*').order('name'));
}

// เปลี่ยนวิธีขนส่ง sea↔air — RPC จัดการช่องเอกสาร B/L↔AWB ให้ถูก (deploy-24)
export async function setTransportMode(shipmentId, mode) {
  const { error } = await supabase.rpc(rpcName('set_transport_mode'), { p_shipment: shipmentId, p_mode: mode });
  err(error);
}

export async function createShipment(fields, actor) {
  const { data, error } = await tbl('shipment').insert(fields).select().single();
  err(error);
  await logEvent(data.id, null, 'shipment_created', 'ordered', actor, { code: data.code });
  await computeRequirements(data.id);   // โชว์ช่องระดับตู้ (booking / ใบตรวจรับ) ทันที
  return data;
}

export async function updateShipment(id, fields, { recompute } = {}) {
  const { error } = await tbl('shipment').update(fields).eq('id', id);
  err(error);
  if (recompute) await computeRequirements(id);   // เปลี่ยน mode → AWB/BL ใหม่
}

export async function updateConsignment(id, fields, shipmentId, { recompute, actor } = {}) {
  // .select('id') = ให้ฐานตอบกลับว่าเขียนจริงกี่แถว — update ที่ RLS กันไว้ได้ 0 แถวแบบไม่มี error
  //   ถ้าไม่เช็ค หน้าเว็บจะปิดหน้าต่างเหมือนบันทึกสำเร็จ ทั้งที่ไม่มีอะไรถูกเขียน
  const { data, error } = await tbl('consignment').update(fields).eq('id', id).select('id');
  err(error);
  if (!data?.length) throw new Error('ไม่ได้บันทึก — ไม่พบรายการนี้หรือบัญชีนี้ไม่มีสิทธิ์แก้ ลองรีเฟรชหน้าแล้วทำใหม่');
  if (recompute && shipmentId) {   // เปลี่ยนประเทศ → CO ใหม่
    // "ไม่ใช้สิทธิ์ FTA" ตัดสินไว้กับประเทศเดิม — เปลี่ยนประเทศต้นทาง = Form ของประเทศใหม่อาจใช้ได้ ต้องตัดสินใหม่
    //   (compute_requirements ไม่แตะ status · ถ้าไม่คืนตรงนี้ ช่อง CO ของประเทศใหม่จะเงียบไปเลย — รีวิว 29 ต.ค. 69)
    const { data: co } = await tbl('document_requirement')
      .select('id, consignment_id, status, matched_document_id')
      .eq('consignment_id', id).eq('req_type', 'certificate_of_origin').maybeSingle();
    //   บันทึกเป็น fta_reset ไม่ใช่ fta_claimed — ประเทศใหม่อาจไม่มี FTA เลย ห้ามเขียนว่า "ต้องมี Form" (รีวิวรอบสาม)
    if (co?.status === 'na') await setFtaWaived(co, false,
      { shipmentId, actor, event: 'fta_reset', reason: `เปลี่ยนประเทศต้นทางเป็น ${fields.origin_country || 'ไม่ระบุ'}` });
    await computeRequirements(shipmentId);
  }
}

export async function addConsignment(shipmentId, fields, actor) {
  const { data, error } = await tbl('consignment')
    .insert({ ...fields, shipment_id: shipmentId }).select().single();
  err(error);
  await computeRequirements(shipmentId);
  await logEvent(shipmentId, data.id, 'consignment_added', null, actor, { supplier: data.supplier_name });
  return data;
}

export async function computeRequirements(shipmentId) {
  const { error } = await supabase.rpc(rpcName('compute_requirements'), { p_shipment: shipmentId });
  err(error);
}

// ปิดงาน = รับของเข้าคลังแล้ว → status received (ย้ายไปแท็บ "เสร็จแล้ว")
export async function closeShipment(shipmentId, actor) {
  await logEvent(shipmentId, null, 'received', 'received', actor, {});
}
// เปิดงานกลับ (เผื่อปิดผิด)
export async function reopenShipment(shipmentId, actor) {
  await logEvent(shipmentId, null, 'reopened', 'docs_received', actor, {});
}
export function isDone(status) { return status === 'received' || status === 'delivered'; }

// ลบ consignment (เผื่อกดผิด/ลองกด) — RPC ข้าม trigger append-only ของ event
export async function deleteConsignment(consignmentId) {
  const { error } = await supabase.rpc(rpcName('delete_consignment'), { p_consignment: consignmentId });
  err(error);
}

// ลบตู้ (soft-delete = void) — หายจากทุกแท็บ (listShipments กรอง status<>void), กู้คืนได้โดย admin
export async function voidShipment(shipmentId) {
  const { error } = await tbl('shipment').update({ status: 'void' }).eq('id', shipmentId);
  err(error);
}

// เปิด/ปิดเอกสารประกันภัยขนส่งของตู้ (optional — บางตู้มี บางตู้ไม่มี)
export async function setShipmentInsurance(shipmentId, on) {
  const existing = await rows(tbl('document_requirement').select('id')
    .eq('shipment_id', shipmentId).is('consignment_id', null).eq('req_type', 'insurance'));
  if (on && !existing.length) {
    const { error } = await tbl('document_requirement').insert({
      shipment_id: shipmentId, req_type: 'insurance', needed_by: 'before_clear', required: true,
    });
    err(error);
  } else if (!on && existing.length) {
    const { error } = await tbl('document_requirement').delete()
      .eq('shipment_id', shipmentId).is('consignment_id', null).eq('req_type', 'insurance');
    err(error);
  }
}

export async function logEvent(shipmentId, consignmentId, type, toStatus, actor, payload = {}) {
  const { error } = await tbl('shipment_event').insert({
    shipment_id: shipmentId, consignment_id: consignmentId,
    event_type: type, to_status: toStatus,
    actor_id: actor?.id ?? null, actor_role: actor?.role ?? null, payload,
  });
  err(error);
}

export async function listEvents(shipmentId) {
  return rows(tbl('shipment_event').select('*').eq('shipment_id', shipmentId)
    .order('id', { ascending: false }).limit(100));
}

// ── shipment journal ─────────────────────────────────────────────────────────
export async function listJournal(shipmentId) {
  return rows(tbl('v_shipment_journal').select('*').eq('shipment_id', shipmentId)
    .order('id', { ascending: false }).limit(200));
}

export async function addNote(shipmentId, { kind, text, watch, consignmentId, classificationId, hsCode, productName }, actor) {
  const payload = { kind, text, watch };
  if (classificationId) payload.classification_id = classificationId;
  if (hsCode) payload.hs_code = hsCode;
  if (productName) payload.product_name = productName;
  await logEvent(shipmentId, consignmentId ?? null, 'note', null, actor, payload);
}

// แก้ไข/ลบ note ที่จดไปแล้ว (RPC ข้าม trigger append-only)
export async function editNote(eventId, text) {
  const { error } = await supabase.rpc(rpcName('edit_note'), { p_event: eventId, p_text: text });
  err(error);
}
export async function deleteNote(eventId) {
  const { error } = await supabase.rpc(rpcName('delete_note'), { p_event: eventId });
  err(error);
}

export async function getWatchouts(supplierNames, currentShipmentId) {
  if (!supplierNames?.length) return [];
  const data = await rows(tbl('v_watchouts').select('*').in('supplier_name', supplierNames)
    .order('created_at', { ascending: false }).limit(50));
  return data.filter(w => w.from_shipment_id !== currentShipmentId);
}

export async function getProductWatchouts({ classIds, hsCodes }, currentShipmentId) {
  if (!classIds?.length && !hsCodes?.length) return [];
  const data = await rows(tbl('v_product_watchouts').select('*')
    .order('created_at', { ascending: false }).limit(80));
  const cset = new Set(classIds ?? []), hset = new Set(hsCodes ?? []);
  return data.filter(w => w.from_shipment_id !== currentShipmentId &&
    ((w.classification_id && cset.has(w.classification_id)) || (w.hs_code && hset.has(w.hs_code))));
}

export async function getShipmentProducts(consignmentIds) {
  if (!consignmentIds?.length) return [];
  const items = await rows(tbl('line_item')
    .select('classification_id, consignment_id, description').in('consignment_id', consignmentIds));
  const classIds = [...new Set(items.map(i => i.classification_id).filter(Boolean))];
  const classes = classIds.length
    ? idx(await rows(tbl('product_classification').select('id, hs_code, name_th, name_en').in('id', classIds)))
    : {};
  const seen = new Set(), out = [];
  for (const li of items) {
    if (!li.classification_id || seen.has(li.classification_id)) continue;
    seen.add(li.classification_id);
    const pc = classes[li.classification_id] ?? {};
    out.push({
      classificationId: li.classification_id, consignmentId: li.consignment_id,
      hsCode: pc.hs_code ?? null, name: itemName(pc, li.description || 'สินค้า'), nameTh: itemNameTh(pc),
    });
  }
  return out;
}

// ── documents: upload → จับคู่การ์ด ──────────────────────────────────────────
export async function uploadDocument({ shipment, consignmentId, reqType, file, actor }) {
  file = await toUploadableImage(file);   // รูป → JPEG ย่อขนาดอัตโนมัติ (PDF/อื่นๆ ผ่านเลย)
  const path = `${safeName(shipment.code)}/${Date.now()}_${safeName(file.name)}`;
  const { error: upErr } = await supabase.storage.from('inbound-docs').upload(path, file);
  err(upErr);
  const { data, error } = await tbl('document').insert({
    shipment_id: shipment.id, consignment_id: consignmentId ?? null,
    req_type: reqType ?? null, file_path: path, file_name: file.name,
    mime_type: file.type, uploaded_by: actor?.id ?? null,
  }).select().single();
  err(error);
  return data;
}

export async function matchDocument(requirement, documentId, actor, shipmentId) {
  const { error } = await tbl('document_requirement')
    .update({ matched_document_id: documentId, status: 'received' }).eq('id', requirement.id);
  err(error);
  await tbl('document').update({
    req_type: requirement.req_type, consignment_id: requirement.consignment_id,
  }).eq('id', documentId);
  await logEvent(shipmentId, requirement.consignment_id, 'doc_matched', null, actor,
    { req_type: requirement.req_type });
}

// ไม่ใช้สิทธิ์ FTA ของ supplier ใบนี้ (ไม่ต้องมี Form) / กลับมาใช้สิทธิ์ — 29 ต.ค. 69
//   user: "ตู้ SP-261015 ถูกส่งมาจากญี่ปุ่น ของข้างในไม่ได้ผลิตในญี่ปุ่นทั้งหมด เลยไม่มีสิทธิ์ของ Form"
//   ระบบตัดสินว่าต้องมี Form จาก "ประเทศต้นทางของ supplier" อย่างเดียว (compute_requirements) แต่สิทธิ์ FTA
//   ดูจากประเทศที่ "ผลิต" ของแต่ละชิ้น — ของผลิตจีนที่ส่งจากญี่ปุ่นใช้ Form JTEPA ไม่ได้ ⇒ ให้คนตัดสินแล้วบันทึกไว้
//   เก็บเป็น status = 'na' ของช่อง CO (listShipments แปลงเป็นไม่ต้องใช้) + จดลงบันทึกตู้ว่าใครตัดสินเพราะอะไร
export async function setFtaWaived(req, waive, { shipmentId, actor, reason, event } = {}) {
  // กลับมาใช้สิทธิ์: คืนสถานะตามของจริงที่มีอยู่ — เคยยืนยัน Form แล้ว = ยืนยันแล้ว · มีไฟล์จับคู่ = ได้ไฟล์แล้ว · ไม่งั้นยังขาด
  //   🐛 รีวิว 29 ต.ค.: เดิมดูแค่ไฟล์ → Form ที่ยืนยันจากสแกนทางอีเมล (ไม่มีไฟล์ในช่อง) กลับมาเป็น "ยังขาด" ถาวร
  //   (ปุ่ม "ตรวจ Form" ไม่ขึ้นอีกเพราะใบรับรองยืนยันแล้ว แต่ช่องเตือนว่าขาด + ตู้กลายเป็นเร่งด่วน)
  let status = 'na';
  if (!waive) {
    const cert = await getCO(req.consignment_id);
    status = cert?.verified ? 'verified' : req.matched_document_id ? 'received' : 'missing';
  }
  const { data, error } = await tbl('document_requirement').update({ status }).eq('id', req.id).select('id');
  err(error);
  if (!data?.length) throw new Error('บันทึกไม่สำเร็จ — ช่องนี้อาจถูกลบไปแล้ว หรือบัญชีนี้แก้ไม่ได้ ลองรีเฟรชหน้า');
  await logEvent(shipmentId, req.consignment_id, event ?? (waive ? 'fta_waived' : 'fta_claimed'), null, actor,
    { reason: reason || null, ...(waive ? { prev_status: req.status ?? null } : { status }) });
}

// การ์ดใบอนุญาตที่รอบนี้ไม่ใช้ (เช่น ชิปปิ้งแจ้งว่าไม่ต้องยื่น) — ต้องมีเหตุผล เก็บในบันทึกตู้ · compute ไม่เขียน status ทับ
export async function setPermitWaived(req, waive, { shipmentId, actor, reason } = {}) {
  const status = waive ? 'na' : (req.matched_document_id ? 'received' : 'missing');
  const { data, error } = await tbl('document_requirement').update({ status }).eq('id', req.id).select('id');
  err(error);
  if (!data?.length) throw new Error('บันทึกไม่สำเร็จ — ช่องนี้อาจถูกลบไปแล้ว หรือบัญชีนี้แก้ไม่ได้');
  try {
    await logEvent(shipmentId, req.consignment_id, waive ? 'permit_waived' : 'permit_restored', null, actor,
      { req_type: req.req_type, side: req.side ?? null, reason: reason || null });
  } catch (e) {
    // บันทึกเหตุผลไม่ได้ = ไม่ยอมให้การ์ดหายจากรายการขาดโดยไม่มีที่มา
    await tbl('document_requirement').update({ status: req.status ?? 'missing' }).eq('id', req.id);
    throw e;
  }
}

// ของในแต่ละ supplier ผลิตที่ประเทศไหนบ้าง (นับเป็นบรรทัด ตามประเทศในใบข้อมูลสินค้า) → { consignment_id: { CN: 95, VN: 19, '?': 2 } }
//   '?' = บรรทัดที่ยังไม่มีใบข้อมูลสินค้า หรือใบยังไม่ระบุประเทศ
//   ใช้บอกใต้ Console ว่า Form ของประเทศต้นทางใช้ได้กับของกี่รายการ
export async function getConsignmentOrigins(consignmentIds) {
  if (!consignmentIds?.length) return {};
  const items = await rows(tbl('line_item').select('consignment_id, classification_id')
    .in('consignment_id', consignmentIds));
  const ids = [...new Set(items.map(i => i.classification_id).filter(Boolean))];
  const origin = {};
  for (let i = 0; i < ids.length; i += 200) {
    const got = await rows(tbl('product_classification').select('id, origin_country').in('id', ids.slice(i, i + 200)));
    for (const c of got) origin[c.id] = String(c.origin_country ?? '').trim().toUpperCase();
  }
  const out = {};
  for (const li of items) {
    const o = (li.classification_id && origin[li.classification_id]) || '?';
    const m = (out[li.consignment_id] ??= {});
    m[o] = (m[o] ?? 0) + 1;
  }
  return out;
}

export async function setRequirementStatus(reqId, status, reason) {
  const { error } = await tbl('document_requirement')
    .update({ status, ...(reason !== undefined ? { conditional_reason: reason } : {}) }).eq('id', reqId);
  err(error);
}

// ลบไฟล์ที่ค้างใน tray (ยังไม่จับคู่) — เผื่ออัพผิด/ซ้ำ
export async function deleteDocument(documentId) {
  // ถ้ามี requirement ชี้ไฟล์นี้ ให้คืนช่องเป็น missing ก่อน
  await tbl('document_requirement')
    .update({ matched_document_id: null, status: 'missing' }).eq('matched_document_id', documentId);
  const { error } = await tbl('document').delete().eq('id', documentId);
  err(error);
}

export async function documentUrl(path) {
  const { data, error } = await supabase.storage.from('inbound-docs').createSignedUrl(path, 3600);
  err(error);
  return data.signedUrl;
}

// สร้างชุดเอกสาร (ZIP ไฟล์ที่อัพโหลดทั้งหมดของตู้) — ขอได้เสมอ
export async function buildPacket(shipment) {
  const { default: JSZip } = await import('jszip');
  const docs = await getDocuments(shipment.id);
  if (!docs.length) throw new Error('ยังไม่มีไฟล์เอกสารในตู้นี้');
  const zip = new JSZip();
  const used = new Set();
  for (const d of docs) {
    try {
      const url = await documentUrl(d.file_path);
      const blob = await (await fetch(url)).blob();
      let name = `${(d.req_type || 'doc')}_${d.file_name || 'file'}`.replace(/[\\/:*?"<>|]/g, '-');
      let n = name, i = 1;
      while (used.has(n)) { n = name.replace(/(\.[^.]+)?$/, `_${i++}$1`); }
      used.add(n);
      zip.file(n, blob);
    } catch { /* ข้ามไฟล์ที่โหลดไม่ได้ */ }
  }
  const out = await zip.generateAsync({ type: 'blob' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(out);
  a.download = `${shipment.code}_เอกสาร.zip`;
  a.click();
  URL.revokeObjectURL(a.href);
  return docs.length;
}

// ── CO verify (hard gate) ────────────────────────────────────────────────────
export async function getCO(consignmentId) {
  const { data, error } = await tbl('certificate_of_origin')
    .select('*').eq('consignment_id', consignmentId).maybeSingle();
  err(error);
  return data;
}

export async function verifyCO({ consignmentId, shipmentId, formType, referenceNo,
  originCriteria, issuedDate, notes, actor }) {
  const base = {
    consignment_id: consignmentId, form_type: formType, reference_no: referenceNo,
    origin_criteria: originCriteria, issued_date: issuedDate || null,
    verified: true, verified_by: actor?.id ?? null,
    verified_at: new Date().toISOString(), verify_notes: notes,
  };
  const existing = await getCO(consignmentId);
  if (existing) {
    const { error } = await tbl('certificate_of_origin').update(base).eq('id', existing.id);
    err(error);
  } else {
    const { error } = await tbl('certificate_of_origin').insert(base);
    err(error);
  }
  const { data: coReq } = await tbl('document_requirement')
    .select('id').eq('consignment_id', consignmentId)
    .eq('req_type', 'certificate_of_origin').maybeSingle();
  if (coReq) await setRequirementStatus(coReq.id, 'verified');
  await logEvent(shipmentId, consignmentId, 'form_verified', 'form_verified', actor,
    { form_type: formType, reference_no: referenceNo });
}

// ── product classification (master) + dossier ────────────────────────────────
// ตัวแทน "ไม่ระบุแบรนด์" (brand is null) สำหรับ sidebar เลือกกรอง
// ⚠ เขียนเป็น \u0000 ห้ามใส่ไบต์ NUL ดิบ — ไบต์ดิบทำให้ grep มองไฟล์นี้เป็น binary แล้วข้ามเงียบ ๆ
//   (26 ต.ค. 69: การตรวจด้วย grep จึงไม่เห็นว่าไฟล์นี้เรียก itemName โดยไม่ import → หน้าสมุดบันทึกตู้พัง)
export const NO_BRAND = '\u0000none';

// brand: undefined/null = ทุกแบรนด์ · NO_BRAND = ที่ไม่มีแบรนด์ · อื่นๆ = กรองแบรนด์นั้น (server-side)
export const NO_CAT = ' none';
// ── ค้นหน้าสินค้า ─────────────────────────────────────────────────────────────
// 28 ต.ค. 69 user: "ใส่ sku แล้ว search ไม่เจอ" — ดู log จริงของ Supabase แล้วเจอ 3 ต้นเหตุ:
//   ① พิมพ์ตอนแป้นยังเป็นไทย ("ะฟาริร" = takibi) → ค้นคำที่พิมพ์ก่อน ไม่เจอเลยค่อยค้นคำที่แปลงกลับเป็นอังกฤษ (lib/kbd.js)
//      (ไม่ค้นพร้อมกัน: คำไทยจริงอย่าง "เสื้อกั๊ก" แปลงได้ "glnhvdyud" → ขึ้นป้าย "แป้นเป็นไทยอยู่ไหม" ทั้งที่พิมพ์ถูก — เจอในฮาร์เนส)
//   ② ช่องว่างหัวท้ายจากการวาง (paste) → ตัดทิ้ง · ช่องว่างซ้อน → ยุบ
//   ③ (ฝั่งหน้าจอ) เปิดจากใบข้อมูลสินค้า รหัสขึ้นในช่องค้นแต่ไม่ได้ค้นจริง + คำตอบเก่าที่มาช้าทับคำตอบใหม่ — ดู Classifications.jsx
// ค่าใส่ "…" ทุกตัว: คำค้นที่มี , . : ( ) ทำให้ไวยากรณ์ or() ของ PostgREST พัง
export function classSearchTerms(search) {
  const q = String(search ?? '').trim().replace(/\s+/g, ' ');
  return { q, alt: q ? thaiLayoutToEn(q) : null };
}
const pgq = (v) => `"${String(v).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
// รหัสที่ตัวคั่นไม่ตรงกับใบ (พิมพ์ติดกัน "VT26SU00203066" · PDF เว้นวรรค "AC 24SU10500CH" · ขีดเกิน "AC-24SU-10500CH")
//   → ตัดตัวคั่นทิ้งแล้วยอมให้มีขีด/ช่องว่าง/จุด/ขีดล่างคั่นระหว่างตัวไหนก็ได้ (รีวิว 28 ต.ค.: เดิมรับเฉพาะแบบติดกัน)
const skuLoose = (t) => {
  const k = String(t).replace(/[\s._\-/]+/g, '');
  return (/^[a-z0-9]{5,}$/i.test(k) && /\d/.test(k) && /[a-z]/i.test(k)) ? [...k].join('[-_ ./]*') : null;
};
function classSearchOr(search) {
  const { q } = classSearchTerms(search);
  if (!q) return '';
  const parts = ['sku', 'name_en', 'name_th', 'brand'].map(col => `${col}.ilike.${pgq(`%${q}%`)}`);
  const loose = skuLoose(q);
  if (loose) parts.push(`sku.imatch.${pgq(loose)}`);
  return parts.join(',');
}

export async function listClassifications(search = '', brand = null, category = null) {
  let q = tbl('product_classification').select('*')
    .order('updated_at', { ascending: false }).limit(500);
  if (brand === NO_BRAND) q = q.is('brand', null);
  else if (brand) q = q.eq('brand', brand);
  if (category === NO_CAT) q = q.is('category', null);
  else if (category) q = q.eq('category', category);
  const or = classSearchOr(search);
  if (or) q = q.or(or);
  return rows(q);
}

// ใบเดียวตาม id — เปิดหน้าเอกสารใบอนุญาตจากลิงก์ /permit/<id> (รีเฟรชแล้วกลับหน้าเดิม) · ไม่เจอ = null
export async function getClassification(id) {
  if (!id) return null;
  const { data, error } = await tbl('product_classification').select('*').eq('id', id).maybeSingle();
  err(error);
  return data;
}

// ค้นในแบรนด์/หมวดที่เลือกแล้วไม่เจอ → นับว่าที่อื่นมีไหม (บอกคนแทนที่จะขึ้นแค่ "ไม่พบ")
//   brand ใส่มา = นับเฉพาะในแบรนด์นั้น (ใช้ตอนเลือกหมวดไว้ — ของอาจอยู่หมวดอื่นของแบรนด์เดียวกัน)
export async function countClassSearch(search, brand = null) {
  const or = classSearchOr(search);
  if (!or) return 0;
  let q = tbl('product_classification').select('id', { count: 'exact', head: true }).or(or);
  if (brand === NO_BRAND) q = q.is('brand', null);
  else if (brand) q = q.eq('brand', brand);
  const { count, error } = await q;
  err(error);
  return count ?? 0;
}

// จำนวนสินค้าต่อแบรนด์ (จาก view — เบา ไม่ดึงทุกแถว) → sidebar
export async function classificationBrands() {
  return rows(tbl('v_classification_brands').select('*'));
}

// จำนวนสินค้าต่อ (แบรนด์, หมวด) จาก view — rail 2 ชั้น
export async function classificationTree() {
  return rows(tbl('v_classification_tree').select('*'));
}

// แปลงป้ายรุ่น PD → ชื่อแบรนด์สะอาด (ตัดปี/คำรุ่น — regex เดียวกับ backfill ใน deploy-20)
export function cleanBrandLabel(label) {
  const n = (label || '').replace(/\s*(20\d{2}(\s*-\s*20\d{2})?|SS\d{2}|AW\d{2}|AU\d{2}|FW\d{2}|\d{2}SS|\d{2}AW|Wear|Gear|Apparel|Accessory|Collection)\s*/gi, ' ')
    .replace(/\s+/g, ' ').trim();
  return n || (label || '').trim();
}
// จากแถว ib_pd_products → {brand, category} ที่จะเก็บลงคลัง
export function pdBrandCategory(r) {
  return {
    brand: r.brand_label || cleanBrandLabel(r.category_root || r.brand) || null,
    category: r.category || r.brand || null,   // ไม่มีหมวด PD = ใช้ป้ายรุ่นเดิมเป็นหมวด
  };
}

// ── คลังวัสดุ (material vocabulary) — พนักงานกดเลือก/พิมพ์เพิ่ม (จำไว้)/แก้/ลบ ──
export async function listMaterials() {
  return rows(tbl('material_option').select('id, name, sort_order')
    .order('sort_order', { ascending: true, nullsFirst: false }).order('name'));
}
export async function addMaterial(name) {
  const n = (name || '').trim();
  if (!n) return null;
  const { data, error } = await tbl('material_option').insert({ name: n }).select('id, name').single();
  if (error) { if (error.code === '23505') return null; err(error); }   // ชื่อซ้ำ = ไม่เพิ่ม (มีอยู่แล้ว)
  return data;
}
export async function renameMaterial(id, name) {
  const n = (name || '').trim();
  if (!n) return;
  const { error } = await tbl('material_option').update({ name: n }).eq('id', id);
  err(error);
}
export async function deleteMaterial(id) {
  const { error } = await tbl('material_option').delete().eq('id', id);
  err(error);
}

export async function saveClassification(fields, id) {
  const q = id
    ? tbl('product_classification').update(fields).eq('id', id)
    : tbl('product_classification').insert(fields);
  const { data, error } = await q.select().single();
  err(error);
  return data;
}

// ── "ของรุ่นเดียวกัน ต่างแค่สี/ไซส์" — กรอกครั้งเดียว เติมต่อให้ทั้งรุ่น ────────
// ใบ SP-261015 มี 114 บรรทัด แต่เป็นของแค่ 36 รุ่น (วัดจริง 25 ต.ค. 69) ⇒ ลดแรงพิมพ์ 68%
// ดึงเฉพาะช่องที่ต้องใช้ตัดสินใจ ไม่ดึง photos/permit_docs — จะหนักเปล่า ๆ
// hs_confirmed: เปลี่ยนพิกัดของใบอื่น → ปลดคำยืนยัน broker ของใบนั้น · updated_at: กันเขียนทับใบที่มีคนแก้ระหว่างนั้น
const SIB_COLS = 'id, sku, brand, name_en, name_th, category, what_is_it, material, ' +
  'usage_desc, origin_country, power_spec, hs_code, hs_confirmed, updated_at';
const SIB_CAP = 2000;

// แคบวงด้วยรหัสขึ้นต้น 6 ตัวแรก (กฎจับรุ่นใน siblings.js ต้องการรหัสซ้ำ ≥6 ตัวอยู่แล้ว) + แบรนด์เดียวกัน
//   → คิวรีเล็ก ไม่ชนเพดานแถว
// ⚠ เทียบแบบเดียวกับ siblingTier (รีวิว 28 ต.ค.): รหัสไม่สนตัวคั่น ("CH 26SU…" ที่มาจาก PDF = "CH-26SU…") ·
//   แบรนด์ไม่สนตัวพิมพ์ ("SNOW PEAK" = "Snow Peak") — เดิมเทียบตรงตัว ใบแบบนั้นไม่โผล่ในแผงเลย
export async function listSiblingPool(card) {
  // ตัวคั่นชุดเดียวกับ skuKey ใน siblings.js · รหัสสั้น (TW-471) ใช้ต้นรหัสสั้นลง ไม่งั้นพี่น้องหลุด (รีวิวรอบสอง)
  const full = String(card?.sku ?? '').replace(/[\s._-]+/g, '');
  const key = full.slice(0, Math.min(6, Math.max(3, full.length - 3)));
  let q = tbl('product_classification').select(SIB_COLS).order('sku').limit(SIB_CAP);
  const brand = String(card?.brand ?? '').trim();
  q = brand ? q.ilike('brand', brand.replace(/[\\%_]/g, (m) => '\\' + m)) : q.is('brand', null);
  if (key.length >= 3) {
    const re = '^' + [...key].map(ch => ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('[-_ ./]*');
    q = q.filter('sku', 'imatch', re);
  }
  const data = await rows(q);
  return { pool: data, truncated: data.length >= SIB_CAP };
}

// เขียนทีละใบ — patch มาจาก planFor() ใน siblings.js (เติมช่องว่าง + ช่องที่คนเพิ่งแก้แล้วติ๊กให้เปลี่ยนตาม)
// ⚠ ไม่ก๊อป hs_confirmed / is_dangerous_goods: สองช่องนั้นเป็น "คำยืนยันของคน" ไม่ใช่คุณสมบัติสินค้า
//   (planFor ส่ง hs_confirmed=false มาได้อย่างเดียว — ตอนพิกัดของใบนั้นถูกเปลี่ยน)
// ⚠ updated_at ต้องตรงกับตอนที่โชว์ให้คนเห็น: มีคนแก้ใบนั้นระหว่างนั้น = ข้าม ไม่ทับ (เขียนทับค่าที่คนไม่ได้เห็นไม่ได้)
//   + .select('id') — 0 แถว = ไม่ได้เขียนจริง (สิทธิ์/ถูกแก้) ต้องบอก ไม่นับว่าสำเร็จ
export async function fillSiblings(plan) {
  let done = 0;
  const failed = [];
  for (const { id, patch, label, updated_at } of plan ?? []) {
    if (!id || !patch || !Object.keys(patch).length) continue;
    let q = tbl('product_classification').update(patch).eq('id', id);
    if (updated_at) q = q.eq('updated_at', updated_at);
    const { data, error } = await q.select('id');
    if (error) failed.push({ id, label, msg: error.message });
    else if (!data?.length) failed.push({ id, label, msg: 'มีคนแก้รายการนี้ระหว่างนั้น — ไม่ได้เขียนทับ ลองเปิดดูแล้วทำใหม่' });
    else done++;
  }
  return { done, failed };
}

// ลบสินค้าออกจากคลัง (เผื่อใส่ผิด/เลิกผลิต) — ถ้าถูกใช้ในตู้อยู่ FK จะกัน (บอกผู้ใช้ให้เอาออกจากตู้ก่อน)
export async function deleteClassification(id) {
  const { error } = await tbl('product_classification').delete().eq('id', id);
  if (error) {
    if (error.code === '23503')
      throw new Error('สินค้านี้ถูกใช้อยู่ในตู้ — เอาสินค้าออกจากใบข้อมูลสินค้าของตู้ก่อน แล้วค่อยลบ');
    throw error;
  }
}

// ค้นสินค้าจาก PD (catalog ของ Purim) เพื่อ sync เข้าคลัง — read-only
const PD_COLS = 'sku, name_th, name_en, brand, category, category_root, brand_label, image_url';
export async function searchPdProducts(term) {
  let q = tbl('pd_products').select(PD_COLS).limit(40);
  if (term) q = q.or(`sku.ilike.%${term}%,name_th.ilike.%${term}%,brand.ilike.%${term}%`);
  else q = q.order('sku').limit(40);
  return rows(q);
}

// ── ผู้จัดทำเอกสาร (ต่อ user) — เก็บชื่อ/ตำแหน่งไว้ เติมลงเอกสารอัตโนมัติ ──────────
export async function getMySigner() {
  const r = await rows(tbl('signer').select('full_name, job_title').limit(1));
  return r[0] ?? null;
}
export async function saveMySigner(name, title) {
  const { error } = await supabase.rpc(rpcName('save_signer'), { p_name: name ?? '', p_title: title ?? '' });
  err(error);
}

// หา SKU ตรงตัวใน Product Manager → prefill ตอนพิมพ์ SKU (ไม่มี = null, ให้กรอกเอง)
export async function lookupPdBySku(sku) {
  const s = (sku ?? '').trim();
  if (!s) return null;
  // ไม่สนพิมพ์เล็ก/ใหญ่ (ilike) — escape % _ กัน over-match แล้วยืนยันตรงเป๊ะแบบ case-insensitive
  const esc = s.replace(/([%_\\])/g, '\\$1');
  const r = await rows(tbl('pd_products').select(PD_COLS).ilike('sku', esc).limit(5));
  return r.find(x => (x.sku ?? '').trim().toLowerCase() === s.toLowerCase()) ?? null;
}

// แบรนด์ที่เคยใช้ (คลัง + PD) → autocomplete
export async function knownBrands() {
  const [cls, pd] = await Promise.all([
    rows(tbl('product_classification').select('brand').not('brand', 'is', null)),
    rows(tbl('pd_products').select('brand').not('brand', 'is', null).limit(1000)),
  ]).catch(() => [[], []]);
  const set = new Set();
  for (const r of [...cls, ...pd]) if (r.brand?.trim()) set.add(r.brand.trim());
  return [...set].sort((a, b) => a.localeCompare(b)).slice(0, 300);
}

// นำเข้าสินค้าเป็นชุดจากไฟล์ (upsert ตาม sku)
// นำเข้าใบข้อมูลสินค้าทีละหลายใบ (ไฟล์ CSV ในหน้าสินค้า · ปุ่มดึงจาก Product Manager)
// 🚨 เดิม upsert ทั้งก้อนทับใบที่มีอยู่ (รีวิว 29 ต.ค. 69 หลังคืนชื่อไทย 2,724 ใบ):
//   ① ดึงจาก PD ส่ง name_th = null (ช่องไทยของ PD เป็นอังกฤษ) → **ลบชื่อไทยของใบเดิมทิ้ง** แบบเดียวกับที่ทำให้ชื่อไทยหายเมื่อ 21 ก.ค.
//   ② CSV ช่องว่างไม่ใส่ key → supabase-js ส่ง ?columns= เป็นรวมทุก key แล้ว PostgREST ใส่ NULL ให้แถวที่ไม่มี key นั้น
//      = ช่องว่างในไฟล์ลบค่าของใบเดิม
//   ⇒ แยกใบใหม่ (insert จัดกลุ่มตามชุดช่องที่มีจริง) กับใบเดิม (update ทีละใบเฉพาะช่องที่ไฟล์ให้มา)
//   · onlyFillEmpty (ดึงจาก PD) = ใบเดิมเติมเฉพาะช่องที่ว่าง ไม่แตะชื่อ
//   · ชื่อไทยที่ไม่มีอักษรไทยไม่เขียนเลย (ช่องนี้คือชื่อสำแดงศุลกากร)
// คืน { added, updated, skippedTh }
const isBlank = (v) => v == null || (Array.isArray(v) ? !v.length : !String(v).trim());
export async function bulkClassifications(records, { onlyFillEmpty = false } = {}) {
  let skippedTh = 0;
  const bySku = new Map();   // รหัสซ้ำในไฟล์ = แถวหลังชนะ (เหมือน upsert เดิม)
  const noSku = [];          // แถวที่ไม่มีรหัส = ใบใหม่เสมอ (เหมือน upsert เดิม — NULL ไม่ชนใคร) · นำเข้าซ้ำ = ใบซ้ำ
  const enFallbackOf = new WeakMap();   // แถว → ชื่ออังกฤษที่เจอในช่องไทย (ใช้กับใบใหม่เท่านั้น)
  for (const r0 of records || []) {
    const r = {};
    for (const [k, v] of Object.entries(r0)) if (!isBlank(v)) r[k] = v;
    let enFallback = null;
    if ('name_th' in r && !hasThai(r.name_th)) {
      // ชื่ออังกฤษในช่องไทย: ไม่ลงช่องไทย แต่ "ใบใหม่" ที่ไม่มีชื่ออังกฤษใช้เป็นชื่ออังกฤษได้ (ไม่งั้นใบได้ชื่อเป็นรหัส)
      //   ⚠ ใบเดิมห้ามได้ค่านี้ — จะทับชื่ออังกฤษที่มีอยู่ (รีวิวรอบสาม 29 ต.ค.) → เก็บแยกไว้ใช้ตอนสร้างใบใหม่เท่านั้น
      skippedTh++; enFallback = r.name_th; delete r.name_th;
    }
    const row = r.sku ? { ...r, sku: String(r.sku).trim() } : r;
    if (enFallback) enFallbackOf.set(row, enFallback);
    if (row.sku) bySku.set(row.sku, row);
    else if (row.name_en || row.name_th || enFallback) noSku.push(row);
  }
  const recs = [...bySku.values()];
  if (!recs.length && !noSku.length) return { added: 0, updated: 0, skippedTh, noSku: 0 };

  const existing = new Map();
  const skus = recs.map(r => r.sku);
  for (let i = 0; i < skus.length; i += 200) {
    const got = await rows(tbl('product_classification')
      .select('id, sku, brand, category, photos, name_en, name_th').in('sku', skus.slice(i, i + 200)));
    for (const c of got) existing.set(c.sku, c);
  }

  // ใบใหม่ — จัดกลุ่มตามชุดช่อง ให้แต่ละคำขอมีแต่ช่องที่ทุกแถวมีจริง (ช่องที่ไม่มีได้ค่า default ของตาราง ไม่ใช่ NULL)
  const fresh = [...recs.filter(r => !existing.has(r.sku)), ...noSku]
    .map(r => ({ ...r, name_en: r.name_en || enFallbackOf.get(r) || r.name_th || r.sku }));   // name_en บังคับ not-null
  const groups = new Map();
  for (const r of fresh) {
    const k = Object.keys(r).sort().join(',');
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(r);
  }
  for (const g of groups.values()) {
    const { error } = await tbl('product_classification').insert(g);
    err(error);
  }

  // ใบเดิม — แก้เฉพาะช่องที่ให้มา (ดึงจาก PD: เฉพาะช่องที่ใบยังว่าง) · ทีละ 8 ใบพร้อมกัน
  const patches = [];
  for (const r of recs) {
    const c = existing.get(r.sku);
    if (!c) continue;
    const { sku, ...rest } = r;
    const patch = {};
    for (const [k, v] of Object.entries(rest)) {
      if (onlyFillEmpty && (k === 'name_en' || k === 'name_th' || !isBlank(c[k]))) continue;
      patch[k] = v;
    }
    if (Object.keys(patch).length) patches.push([c.id, patch]);
  }
  for (let i = 0; i < patches.length; i += 8) {
    const res = await Promise.all(patches.slice(i, i + 8).map(([id, patch]) =>
      tbl('product_classification').update(patch).eq('id', id)));
    for (const { error } of res) err(error);
  }
  return { added: fresh.length, updated: patches.length, skippedTh, noSku: noSku.length };
}

// ชื่อไฟล์ให้ Supabase storage รับได้ (ตัด ~ และอักขระพิเศษที่ทำให้ "Invalid key")
const safeName = (name) => (name || 'file')
  .normalize('NFC').replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '').slice(-80) || 'file';

export async function uploadClassPhoto(classId, file) {
  const path = `${classId}/${Date.now()}_${safeName(file.name)}`;
  const { error: upErr } = await supabase.storage.from('inbound-photos').upload(path, file);
  err(upErr);
  const { data: pub } = supabase.storage.from('inbound-photos').getPublicUrl(path);
  return pub.publicUrl;
}

// ── เอกสารยื่นขอใบอนุญาต: รูปสินค้าเพิ่ม → bucket สาธารณะ inbound-photos ──
// 🚨 ใช้กับ "รูปสินค้า" เท่านั้น — เอกสาร (ผลทดสอบ/ใบอนุญาต/สเปค) ต้องไป uploadProductFile (ส่วนตัว)
//   เดิมฟังก์ชันนี้รับไฟล์เอกสารด้วย แล้วได้ลิงก์สาธารณะที่ใครก็เปิดได้ (แก้ 26 ต.ค. 69 · deploy-37)
export async function uploadPermitFile(classId, file) {
  if (!file?.type?.startsWith('image/')) throw new Error('ช่องนี้รับเฉพาะรูปสินค้า — ไฟล์เอกสารแนบได้ที่หัวข้อด้านล่าง');
  file = await toUploadableImage(file);
  const path = `${classId}/permit_${Date.now()}_${safeName(file.name)}`;
  const { error: upErr } = await supabase.storage.from('inbound-photos').upload(path, file);
  err(upErr);
  const { data: pub } = supabase.storage.from('inbound-photos').getPublicUrl(path);
  return pub.publicUrl;
}
export async function savePermitDocs(classId, docs) {
  const { error } = await tbl('product_classification').update({ permit_docs: docs }).eq('id', classId);
  err(error);
}
export async function saveClassPhotos(classId, photos) {
  const { error } = await tbl('product_classification').update({ photos }).eq('id', classId);
  err(error);
}

// ── ไฟล์อ้างอิงของสินค้า (deploy-37) → bucket ส่วนตัว inbound-docs เปิดด้วย signed URL ──
// แถวอยู่ตาราง product_file (ไม่ใช่ jsonb ในแถวสินค้า — หน้ารวมดึงทีละ 500 ใบ จะลากรายชื่อไฟล์มาเปล่า ๆ
// และสองคนแนบพร้อมกันจะทับกัน) · ลบแถว = เข้าถังขยะ · ตัวไฟล์ไม่ถูกลบ ⇒ กู้คืนแล้วเปิดได้ทันที
export const PRODUCT_FILE_MAX = 20 * 1024 * 1024;   // ต้องตรงกับ check ใน deploy-37
export const fmtBytes = (n) => {
  if (n == null || !Number.isFinite(Number(n))) return '';
  const v = Number(n);
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${Math.round(v / 1024)} KB`;
  return `${(v / 1024 / 1024).toFixed(v < 10 * 1024 * 1024 ? 1 : 0)} MB`;
};

export async function listProductFiles(classId) {
  if (!classId) return [];
  return rows(tbl('product_file').select('*').eq('classification_id', classId)
    .order('uploaded_at', { ascending: false }));
}

// ชนิดไฟล์ที่รับ — ช่องเลือกไฟล์กรองให้แล้ว แต่ "ลากมาวาง" ข้ามตัวกรองนั้นได้ จึงต้องกันที่นี่อีกชั้น
// ⚠ ห้ามรับ SVG/HTML: ลิงก์ชั่วคราวเปิดไฟล์ "แสดงในแท็บ" บนโดเมน supabase.co → สคริปต์ในไฟล์รันได้ (รีวิว 26 ต.ค.)
const FILE_TYPES = {
  pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
  webp: 'image/webp', heic: 'image/heic', heif: 'image/heif', txt: 'text/plain', csv: 'text/csv',
  doc: 'application/msword', xls: 'application/vnd.ms-excel', ppt: 'application/vnd.ms-powerpoint',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
};
const extOf = (name) => (String(name || '').match(/\.([a-z0-9]+)$/i)?.[1] || '').toLowerCase();
// ชนิดที่ "เปิดดูในแท็บ" ได้ปลอดภัย · ที่เหลือ (Word/Excel/HEIC) = ดาวน์โหลดด้วยชื่อไฟล์จริง
const PREVIEWABLE = /^(application\/pdf|image\/(jpeg|png|gif|webp)|text\/plain)$/i;
export const PRODUCT_FILE_ACCEPT = Object.keys(FILE_TYPES).map(e => '.' + e).join(',');

// คืน MIME ที่ยอมรับ หรือ null ถ้าไม่รับ — เชื่อนามสกุลเฉพาะตอนเบราว์เซอร์ไม่บอกชนิด
function acceptedType(file) {
  const t = (file?.type || '').toLowerCase();
  if (t && t !== 'application/octet-stream') return Object.values(FILE_TYPES).includes(t) ? t : null;
  return FILE_TYPES[extOf(file?.name)] ?? null;
}

export async function uploadProductFile(classId, file, { kind = 'reference', forPermit = false, note = null } = {}) {
  if (!classId) throw new Error('บันทึกสินค้าก่อน แล้วค่อยแนบไฟล์');
  if (!file) return null;
  let type = acceptedType(file);
  if (!type) {
    throw new Error('แนบได้เฉพาะ PDF · รูป (JPG/PNG/HEIC) · Word · Excel · PowerPoint · ไฟล์ข้อความ'
      + (extOf(file.name) ? ` — ไฟล์ .${extOf(file.name)} แนบไม่ได้` : ''));
  }
  // เก็บต้นฉบับเสมอ (สแกนฉลาก/ภาพหน้าจอตารางสเปคต้องอ่านตัวเล็กออก — ย่อแบบรูปสินค้า 1600px จะอ่านไม่ได้)
  // แปลงเฉพาะ HEIC ของ iPhone เพราะเบราว์เซอร์ส่วนใหญ่เปิดไม่ได้ · Chrome ถอดรหัส HEIC ไม่ได้ → เก็บต้นฉบับ
  //   (เปิดแล้วจะดาวน์โหลดแทนแสดงในแท็บ ไม่เสียข้อมูล) · ห้ามแปลงชนิดอื่น: TIFF หลายหน้าจะเหลือหน้าแรก
  if (type === 'image/heic' || type === 'image/heif') {
    const conv = await toUploadableImage(file, { maxDim: 4000, quality: 0.92 });
    if (conv !== file && conv?.type === 'image/jpeg') { file = conv; type = 'image/jpeg'; }
  }
  // ไฟล์ที่เบราว์เซอร์ไม่บอกชนิด จะถูกเก็บเป็น application/octet-stream → PDF เปิดดูไม่ได้ ต้องดาวน์โหลด
  if ((file.type || '').toLowerCase() !== type) file = new File([file], file.name || 'file', { type });
  if (file.size > PRODUCT_FILE_MAX) {
    throw new Error(`ไฟล์ใหญ่ ${fmtBytes(file.size)} — รับได้ไม่เกิน ${fmtBytes(PRODUCT_FILE_MAX)} ต่อไฟล์`
      + ' (PDF ใหญ่ลองบีบอัด หรือแนบเฉพาะหน้าที่เกี่ยวข้อง)');
  }
  // ชื่อไทยล้วนถูก safeName ตัดเหลือแค่นามสกุล (".pdf") → ต่อท้ายสุ่มกันชนกันเมื่อแนบหลายไฟล์ติดกัน
  //   (ชื่อจริงที่คนเห็นเก็บใน file_name — path ใช้แค่เป็นกุญแจใน storage)
  const tag = Math.random().toString(36).slice(2, 7);
  const path = `products/${classId}/${Date.now()}_${tag}_${safeName(file.name)}`;
  const { error: upErr } = await supabase.storage.from('inbound-docs').upload(path, file);
  err(upErr);
  // ⚠ ถ้าบันทึกแถวไม่สำเร็จ ตัวไฟล์จะค้างใน storage (ลบจากหน้าเว็บไม่ได้ — ไม่มี DELETE policy โดยตั้งใจ)
  //   ไม่อันตราย: ไม่มีแถวชี้ = ไม่มีใครเห็นลิงก์ · ขนาดจำกัดที่ 20 MB
  const { data, error } = await tbl('product_file').insert({
    classification_id: classId, file_path: path, file_name: file.name || 'file',
    mime_type: file.type || null, size_bytes: file.size ?? null,
    kind, for_permit: !!forPermit, note: (note || '').trim() || null,
  }).select().single();
  err(error);
  return data;
}

export async function updateProductFile(id, patch) {
  const allowed = {};
  for (const k of ['for_permit', 'note', 'kind']) if (k in patch) allowed[k] = patch[k];
  if ('note' in allowed) allowed.note = (allowed.note || '').trim() || null;
  const { data, error } = await tbl('product_file').update(allowed).eq('id', id).select().single();
  err(error);
  return data;
}

// เอาออก = ลบแถว (เข้าถังขยะ กู้ได้ที่ ตั้งค่า › ถังขยะ) · ตัวไฟล์ใน storage ไม่ถูกลบ
export async function removeProductFile(id) {
  const { error } = await tbl('product_file').delete().eq('id', id);
  err(error);
}

// ลิงก์เปิดไฟล์ 1 ไฟล์ — PDF/รูป/ข้อความเปิดในแท็บ · Word/Excel/HEIC ดาวน์โหลดด้วยชื่อไฟล์จริง
// (path ใน storage ตัดอักษรไทยทิ้ง ไม่งั้นไฟล์ "หนังสือยืนยันวัสดุ.docx" จะถูกบันทึกเป็น ".docx")
export async function productFileOpenUrl(f) {
  const inline = PREVIEWABLE.test(f?.mime_type || '');
  const { data, error } = await supabase.storage.from('inbound-docs')
    .createSignedUrl(f.file_path, 3600, inline ? undefined : { download: f.file_name || true });
  err(error);
  return data.signedUrl;
}

// ลิงก์ชั่วคราว 1 ชม. ทีละหลายไฟล์ในคำขอเดียว → { path: url }
export async function productFileUrls(paths) {
  const list = [...new Set((paths ?? []).filter(Boolean))];
  if (!list.length) return {};
  const { data, error } = await supabase.storage.from('inbound-docs').createSignedUrls(list, 3600);
  err(error);
  const out = {};
  for (const r of data ?? []) if (r?.signedUrl && r.path) out[r.path] = r.signedUrl;
  return out;
}

// รวมไฟล์แนบทั้งหมด (รูป + แคตตาล็อก/สเปค/คู่มือ/ผลทดสอบ) เป็น ZIP ชุดเดียว → ยื่นหน่วยงานได้เลย
const PERMIT_FOLDER = { license: 'ใบอนุญาต', catalog: 'แคตตาล็อก', spec: 'สเปค-กำลังไฟ', manual: 'คู่มือ', test: 'ผลทดสอบ', other: 'เอกสารอื่น', reference: 'เอกสารอ้างอิง' };
// files = แถว product_file · ใส่ซองเฉพาะที่ for_permit = true (ไฟล์อ้างอิงภายในห้ามหลุดไปหน่วยงาน)
export async function downloadPermitBundle(cls, photos = [], docs = [], files = []) {
  const picked = (files ?? []).filter(f => f.for_permit && f.file_path);
  const legacy = docs.filter(d => d.url && !d.field);   // รายการ url สาธารณะแบบเก่า (ก่อน deploy-37)
  const withUrl = [...photos.filter(p => p.url), ...legacy, ...picked];
  if (!withUrl.length) throw new Error('ยังไม่มีไฟล์ที่จะใส่ซอง — แนบรูป หรือติ๊ก "ใส่ในซองยื่น" ที่ไฟล์ก่อน');
  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  const used = new Set();
  // ⚠ ซองนี้ไปหน่วยงานราชการ — ไฟล์ที่โหลดไม่ได้ต้อง "บอก" ไม่ใช่ข้ามเงียบ ๆ
  //   และ fetch ไม่ throw เมื่อได้ 400/404 (storage ตอบ JSON + CORS เปิด) → ถ้าไม่เช็ค res.ok
  //   จะได้หน้า error ถูกบันทึกในซองด้วยชื่อเอกสารจริง (รีวิว 26 ต.ค.)
  const failed = [];
  let added = 0;
  const add = async (folder, name, url) => {
    try {
      if (!url) throw new Error('no url');
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const base = `${folder}/${(name || 'file').replace(/[\\/:*?"<>|]/g, '-')}`;
      let n = base, i = 1;
      while (used.has(n)) n = base.replace(/(\.[^.]+)?$/, `_${i++}$1`);
      used.add(n);
      zip.file(n, blob);
      added++;
    } catch { failed.push(name || 'ไฟล์ไม่มีชื่อ'); }
  };
  let pi = 1;
  for (const p of photos) if (p.url) await add('รูปสินค้า', `photo_${pi++}.jpg`, p.url);
  for (const d of legacy) await add(PERMIT_FOLDER[d.kind] || 'เอกสาร', d.name, d.url);
  if (picked.length) {
    const urls = await productFileUrls(picked.map(f => f.file_path));
    for (const f of picked) await add(PERMIT_FOLDER[f.kind] || 'เอกสารอื่น', f.file_name, urls[f.file_path]);
  }
  if (failed.length) {
    throw new Error(`ยังไม่ได้สร้างซอง — โหลดไฟล์ไม่ได้ ${failed.length} ไฟล์: ${failed.join(' · ')}`
      + ' (ลองกดอีกครั้ง ถ้ายังไม่ได้ให้เอาติ๊ก "ใส่ในซองยื่น" ของไฟล์นั้นออก แล้วแนบใหม่)');
  }

  const tisi = docs.find(d => d.field === 'tisi')?.text;
  const summary = [
    // ⚠ ชื่อไทยก่อน — บรรทัดนี้อยู่ในซองที่ยื่นหน่วยงาน ไม่ใช่หน้าจอพนักงาน (ดู src/lib/naming.js)
    `สินค้า: ${cls.name_th || cls.name_en || ''}  (SKU ${cls.sku || '-'})`,
    cls.brand ? `แบรนด์/รุ่น: ${cls.brand}` : '',
    cls.hs_code ? `พิกัด HS: ${cls.hs_code}` : '',
    hasMaterial(cls) ? `วัสดุ/ส่วนประกอบ: ${String(cls.material).trim()}` : '',
    tisi ? `เลข มอก.: ${tisi}` : '',
    '', 'ชุดเอกสารแนบประกอบคำขอ — สร้างจาก INBOUNDpoint (CAMP STUDIO)',
  ].filter(Boolean).join('\r\n');
  zip.file('ข้อมูลสินค้า.txt', '﻿' + summary);   // BOM ให้ Windows เปิดไทยได้

  const out = await zip.generateAsync({ type: 'blob' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(out);
  a.download = `${(cls.sku || 'permit').replace(/[\\/:*?"<>|]/g, '-')}_เอกสารแนบ.zip`;
  a.click();
  URL.revokeObjectURL(a.href);
  return added;
}

export async function listHsRules() {
  return rows(tbl('hs_rule').select('*').order('hs_code'));
}

// กฎพิกัดทั้งตาราง สำหรับกล่องเรื่องใบอนุญาต — ใช้ซ้ำในเซสชัน 5 นาที (หน้าใบข้อมูลสินค้าโหลดใหม่บ่อย) · พังแล้วไม่จำ กดลองใหม่ได้
//   ⚠ ห้ามใช้ searchHsRules('') มันตัดที่ 50 แถว กฎที่มีมาตรการหลุดเงียบ ๆ
let permitRulesMemo = null;
export function loadPermitRules() {
  if (permitRulesMemo && Date.now() - permitRulesMemo.at < 5 * 60 * 1000) return permitRulesMemo.p;
  const p = listHsRules();
  const memo = { p, at: Date.now() };
  permitRulesMemo = memo;
  p.catch(() => { if (permitRulesMemo === memo) permitRulesMemo = null; });
  return p;
}

// การ์ดเอกสารใบอนุญาตของใบนี้ — บอกในกล่องว่ามีไฟล์แล้วหรือยัง · อ่านไม่ได้ = ไม่โชว์สถานะ ไม่ทำให้กล่องพัง
export async function listPermitReqs(consignmentId) {
  return rows(tbl('document_requirement').select('req_type, side, status, required')
    .eq('consignment_id', consignmentId)
    .in('req_type', ['phytosanitary', 'fumigation', 'permit_forest', 'permit_tisi', 'permit_fda', 'permit_excise', 'import_license_other']));
}

export async function getHsRule(hsCode) {
  if (!hsCode) return null;
  const { data, error } = await tbl('hs_rule').select('*').eq('hs_code', hsCode).maybeSingle();
  err(error);
  return data;
}

export async function searchHsRules(term) {
  let q = tbl('hs_rule').select('*').order('hs_code').limit(50);
  if (term) q = q.or(`hs_code.ilike.%${term}%,description_th.ilike.%${term}%`);
  return rows(q);
}

// พิกัดที่ใช้บ่อย = HS ที่ broker ยืนยันแล้วในคลังสินค้า (นับความถี่)
export async function frequentHs() {
  const cls = await rows(tbl('product_classification')
    .select('hs_code, name_th, name_en').eq('hs_confirmed', true).not('hs_code', 'is', null));
  const agg = {};
  for (const c of cls) {
    const k = c.hs_code;
    if (!agg[k]) agg[k] = { hs_code: k, count: 0, sample: itemName(c) + (itemNameTh(c) ? ` · ไทย: ${itemNameTh(c)}` : '') };
    agg[k].count++;
  }
  return Object.values(agg).sort((a, b) => b.count - a.count).slice(0, 10);
}

// นำเข้าพิกัดทีละเยอะจากไฟล์ — เพิ่มเฉพาะพิกัดใหม่ (ข้ามที่มีอยู่ กันเขียนทับกฎที่ verify ไว้แล้ว)
export async function bulkHsRules(records) {
  const clean = records.filter(r => r.hs_code);
  if (!clean.length) return { added: 0, skipped: 0 };
  const existing = new Set((await rows(tbl('hs_rule').select('hs_code'))).map(r => r.hs_code));
  const fresh = clean.filter(r => !existing.has(r.hs_code));
  if (fresh.length) { const { error } = await tbl('hs_rule').insert(fresh); err(error); }
  return { added: fresh.length, skipped: clean.length - fresh.length };
}

export async function saveHsRule(fields, isNew) {
  permitRulesMemo = null;
  if (isNew) {
    const { error } = await tbl('hs_rule').insert(fields);
    err(error);
  } else {
    const { hs_code, ...rest } = fields;
    const { error } = await tbl('hs_rule')
      .update({ ...rest, updated_at: new Date().toISOString() }).eq('hs_code', hs_code);
    err(error);
  }
}

// ── admin analytics ──────────────────────────────────────────────────────────
export async function analytics() {
  const pick = (v) => rows(tbl(v).select('*'));
  const [yoy, byMonth, byMode, bySupplier, ftaSavings, docQuality] = await Promise.all([
    pick('v_yoy'), pick('v_shipments_by_month'), pick('v_by_mode'),
    pick('v_by_supplier'), pick('v_fta_savings'), pick('v_doc_quality'),
  ]);
  return { yoy, byMonth, byMode, bySupplier, ftaSavings, docQuality };
}

export async function listFtaForms() {
  return rows(tbl('fta_origin_form').select('*').order('origin_country'));
}

export async function auditLog(limit = 100) {
  const events = await rows(tbl('shipment_event').select('*')
    .order('id', { ascending: false }).limit(limit));
  const shipIds = [...new Set(events.map(e => e.shipment_id))];
  const ships = shipIds.length ? idx(await rows(tbl('shipment').select('id, code').in('id', shipIds))) : {};
  for (const e of events) e.shipment = ships[e.shipment_id] ? { code: ships[e.shipment_id].code } : null;
  return events;
}

// dossier = 1 consignment (1 invoice = 1 ใบขน)
export async function getDossier(consignmentId) {
  const { data: c, error } = await tbl('consignment').select('*').eq('id', consignmentId).single();
  err(error);
  const { data: ship } = await tbl('shipment')
    .select('id, code, label, mode, eta, status').eq('id', c.shipment_id).maybeSingle();
  c.shipment = ship ?? null;
  // ลำดับต้องนิ่ง — ไฟล์ Excel ส่งชิปปิ้งมีเลข "No" แล้วไป-กลับกับ Yusen · ไม่ใส่ order = Postgres คืนตามที่เก็บจริง
  //   ซึ่งเปลี่ยนหลัง UPDATE (เช่นปุ่มสร้างใบสินค้า) → เลข No ของรายการเดิมเปลี่ยนระหว่างไฟล์ (รีวิว 28 ต.ค.)
  //   นำเข้า invoice ทีเดียว = created_at เท่ากันทั้งชุด → เรียงตามรหัส (สี/ไซส์ของรุ่นเดียวกันอยู่ติดกัน)
  //   ⚠ ไม่ใช่ลำดับบรรทัดใน invoice เป๊ะ — ต้องเก็บเลขบรรทัดตอนนำเข้าถึงจะได้แบบนั้น (ยังไม่ทำ)
  const items = await rows(tbl('line_item').select('*').eq('consignment_id', consignmentId)
    .order('created_at', { ascending: true })
    .order('sku', { ascending: true, nullsFirst: false })
    .order('id', { ascending: true }));
  const classIds = [...new Set(items.map(i => i.classification_id).filter(Boolean))];
  const classes = classIds.length
    ? idx(await rows(tbl('product_classification').select('*').in('id', classIds)))
    : {};
  for (const li of items) li.product_classification = classes[li.classification_id] ?? null;
  // ที่มาของวัสดุ (ประเภทแหล่ง · คนแก้ค่าไหม) สำหรับคอลัมน์ "แหล่งข้อมูลวัสดุ" ในไฟล์ส่งชิปปิ้ง — อยู่ที่ตารางงานค้น ไม่ได้อยู่บนใบสินค้า
  //   ⚠ ทนกรณียังไม่รัน deploy-38 / บัญชีอ่านไม่ได้: ไม่ได้ข้อมูลก็แค่เหลือลิงก์ ใบข้อมูลสินค้าต้องเปิดได้เสมอ
  //   เก็บไว้ที่ li ไม่ใช่บนใบสินค้า — ของบนใบสินค้าอาจถูกส่งกลับไปเขียนฐานข้อมูล
  const researchIds = [...new Set(Object.values(classes).map(x => x.material_research_id).filter(Boolean))];
  if (researchIds.length) {
    const prov = {};
    try {
      for (let i = 0; i < researchIds.length; i += 200) {
        const got = await rows(tbl('material_research').select('id, source_tier, approved_edited').in('id', researchIds.slice(i, i + 200)));
        for (const r of got) prov[r.id] = r;
      }
    } catch { /* คอลัมน์แหล่งเหลือแค่ลิงก์ */ }
    for (const li of items) li.material_provenance = prov[li.product_classification?.material_research_id] ?? null;
  }
  return { consignment: c, items };
}

export async function addLineItem(consignmentId, fields, shipmentId) {
  const { data, error } = await tbl('line_item')
    .insert({ ...fields, consignment_id: consignmentId }).select().single();
  err(error);
  if (shipmentId) await computeRequirements(shipmentId);  // HS ใหม่ → permit cards
  return data;
}

// เพิ่มสินค้าหลายรายการทีเดียว (multi-select picker)
export async function addLineItems(consignmentId, records, shipmentId) {
  if (!records.length) return 0;
  const { error } = await tbl('line_item')
    .insert(records.map(r => ({ ...r, consignment_id: consignmentId })));
  err(error);
  if (shipmentId) await computeRequirements(shipmentId);
  return records.length;
}

export async function deleteLineItem(id, shipmentId) {
  const { error } = await tbl('line_item').delete().eq('id', id);
  err(error);
  if (shipmentId) await computeRequirements(shipmentId);
}

// นำเข้ารายการสินค้าจากไฟล์ invoice/packing (SKU + qty) → จับคู่กับคลัง (product_classification)
// แล้วสร้าง line_item ให้อัตโนมัติ (คนตรวจ/แก้ทีหลัง). ไม่พบใน SKU คลัง = ใส่เป็นแถวหลวม (ไม่มี classification)
// lines = [{sku, qty, description?, unit?, unit_price?, pack_size?}]
// ⚠ ใส่ราคาด้วยถ้าไฟล์มี — เป็นตัวเดียวที่ทำให้เทียบกับ "ราคาที่ตกลง" ในใบสั่งซื้อได้ (deploy-28)
export async function importInvoiceLines(consignmentId, lines, shipmentId) {
  const clean = (lines || []).filter(r => (r.sku && r.sku.trim()) || (r.description && r.description.trim()));
  if (!clean.length) return { added: 0, matched: 0, unmatched: 0, unmatchedSkus: [] };
  const skus = [...new Set(clean.map(r => (r.sku || '').trim()).filter(Boolean))];
  // 🚨 เดิมลองแค่ 3 แบบ (ตัวเดิม/ใหญ่/เล็ก) แล้วยิง .in() ตรงตัวอักษร ⇒ รหัสที่ขีดหรือเว้นวรรคต่างกัน
  //    พลาดเงียบ ๆ ('AC 24SU10500CH' ไม่เจอ 'AC-24SU10500CH') ซึ่งเป็นรูปที่ไฟล์ PDF ทำบ่อยที่สุด
  //    ตอนนี้ดึงทะเบียนมาเทียบด้วย normSku (ตัดช่องว่าง/จุด/ขีด + ตัวใหญ่) แบบเดียวกับ matchPoLines
  const master = skus.length
    ? await rows(tbl('product_classification').select('id, sku, name_th, name_en, hs_code, what_is_it, material'))
    : [];
  const bySku = {};
  for (const m of master) if (m.sku) bySku[normSku(m.sku)] = m;

  const unmatchedSkus = [];
  let matched = 0, priced = 0;
  const records = clean.map(r => {
    const sku = (r.sku || '').trim();
    const m = sku ? bySku[normSku(sku)] : null;
    const qty = Number(r.qty) > 0 ? Number(r.qty) : 1;
    const unit = (r.unit || '').trim() || 'ชิ้น';
    const price = r.unit_price === '' || r.unit_price == null ? null
      : (Number.isFinite(Number(r.unit_price)) ? Number(r.unit_price) : null);
    if (price != null) priced++;
    // 1 หน่วยบน invoice = กี่ชิ้น
    // ⚠ คอลัมน์นี้เป็น NOT NULL default 1 ในฐานข้อมูล จึงใส่ null ไม่ได้ ต้องลง 1 ไปก่อน
    //   ⇒ "ไม่รู้" กับ "รู้ว่าเป็น 1" แยกกันไม่ออกที่ชั้นข้อมูล — หน้ายืนยันก่อนบันทึกจึงต้อง
    //     โชว์ช่องนี้ให้คนเห็นและแก้ได้ทุกครั้ง (ดู ImportInvoiceModal) ไม่ใช่เติมเงียบ ๆ
    const packRaw = Math.round(Number(r.pack_size));
    const pack = Number.isFinite(packRaw) && packRaw > 0 ? packRaw : 1;
    const base = { consignment_id: consignmentId, qty, unit, sku: sku || null,
      po_ref: sku || null, unit_price: price, pack_size: pack };
    if (m) {
      matched++;
      return { ...base, classification_id: m.id,
        // ⚠ ชื่อไทยก่อน — ค่านี้ลงฐานเป็นชื่อที่ขึ้นบนเอกสารส่ง broker (ดู src/lib/naming.js)
        description: (r.description || '').trim() || m.name_th || m.name_en };
    }
    if (sku) unmatchedSkus.push(sku);
    // ⚠ อย่ายัด "ไม่พบในคลัง" ลงช่อง description — มันคือชื่อสินค้าที่ขึ้นบนเอกสารส่ง broker
    //   และถ้าสร้างใบสินค้าจากบรรทัดนี้ทีหลัง ชื่อใบจะเปื้อนตามไปด้วย (เกิดจริง 25 ต.ค. 69)
    //   สถานะ "ยังไม่มีใบ" ดูจาก classification_id เป็น null ได้อยู่แล้ว ไม่ต้องเขียนทับชื่อ
    const label = (r.description || '').trim() || sku || 'ไม่ระบุ';
    return { ...base, classification_id: null, description: label };
  });

  const { error } = await tbl('line_item').insert(records);
  err(error);
  if (shipmentId) await computeRequirements(shipmentId);
  return { added: records.length, matched, priced, unmatched: unmatchedSkus.length, unmatchedSkus };
}

// ══════════════ Brand Hub — แท็บ "แบรนด์" (deploy-23) ══════════════
// 1 แถว inbound.supplier_profile = 1 แบรนด์/บริษัทคู่ค้า · ชื่อสะกดอื่นเก็บใน aliases
// เชื่อมกับตู้/เอกสาร/บทเรียนด้วย "ชื่อ" (consignment.supplier_name, product_classification.brand)
// เทียบแบบไม่สนตัวพิมพ์/ช่องว่าง — ไม่แก้ชื่อเดิมในตู้ (v_watchouts + ระบบอื่นยึดชื่อเดิม)
export const normName = (s) => (s || '').normalize('NFC').trim().toLowerCase().replace(/\s+/g, ' ');
export const partnerNames = (p) => [p?.supplier_name, ...(p?.aliases ?? [])].filter(Boolean);
export const partnerMatches = (p, name) => {
  const n = normName(name);
  return !!n && partnerNames(p).some(a => normName(a) === n);
};
// ตู้ที่มี consignment ของแบรนด์นี้ (จาก listShipments) — pure
export const partnerShipmentsOf = (p, ships = []) =>
  ships.filter(s => (s.consignment ?? []).some(c => partnerMatches(p, c.supplier_name)));

export async function listPartners() {
  return rows(tbl('supplier_profile').select('*').order('supplier_name'));
}
export async function savePartner(fields, id) {
  const f = { ...fields };
  if (f.supplier_name !== undefined) f.supplier_name = (f.supplier_name || '').trim();
  if (f.aliases) f.aliases = [...new Set(f.aliases.map(a => (a || '').trim()).filter(Boolean))];
  const q = id
    ? tbl('supplier_profile').update({ ...f, updated_at: new Date().toISOString() }).eq('id', id)
    : tbl('supplier_profile').insert(f);
  const { data, error } = await q.select().single();
  if (error) {
    if (error.code === '23505') throw new Error('มีแบรนด์ชื่อนี้อยู่แล้ว — เปิดแบรนด์เดิมแล้วเพิ่ม "ชื่อสะกดอื่น" แทน');
    throw error;
  }
  return data;
}
export async function deletePartner(id) {
  const { error } = await tbl('supplier_profile').delete().eq('id', id);   // cascade: ผู้ติดต่อ + บันทึก; reminder → partner_id null
  err(error);
}

// ── ผู้ติดต่อ ──
export async function listPartnerContacts(partnerId) {
  return rows(tbl('partner_contact').select('*').eq('partner_id', partnerId)
    .order('is_primary', { ascending: false })
    .order('sort_order', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: true }));
}
export async function savePartnerContact(fields, id) {
  const q = id
    ? tbl('partner_contact').update({ ...fields, updated_at: new Date().toISOString() }).eq('id', id)
    : tbl('partner_contact').insert(fields);
  const { data, error } = await q.select().single();
  err(error);
  // ★ หลัก มีได้คนเดียวต่อแบรนด์ — ติ๊กคนใหม่ = ปลดคนเก่า
  if (fields.is_primary && data?.partner_id) {
    const { error: e2 } = await tbl('partner_contact').update({ is_primary: false })
      .eq('partner_id', data.partner_id).neq('id', data.id).eq('is_primary', true);
    err(e2);
  }
  return data;
}
// ชื่อ supplier "ตามที่สะกดจริงในตู้" ของแบรนด์นี้ — v_watchouts/analytics เทียบแบบตรงตัว ต้องส่งชื่อดิบ ไม่ใช่ alias ที่พิมพ์เอง
export const partnerRawNames = (p, ships = []) =>
  [...new Set(ships.flatMap(s => (s.consignment ?? []).map(c => c.supplier_name).filter(n => partnerMatches(p, n))))];
export async function deletePartnerContact(id) {
  const { error } = await tbl('partner_contact').delete().eq('id', id);
  err(error);
}

// ── ประชุม / บันทึก / โทร / อีเมล (timeline ต่อแบรนด์) ──
export async function listPartnerNotes(partnerId) {
  const list = await rows(tbl('partner_note').select('*').eq('partner_id', partnerId)
    .order('created_at', { ascending: false }).limit(300));
  // ปักหมุดก่อน แล้วเรียงตามเวลาที่เกิดจริง (held_at) ใหม่→เก่า; ไม่มี held_at ใช้เวลาสร้าง
  const t = (n) => new Date(n.held_at || n.created_at).getTime();
  return list.sort((a, b) => (Number(b.pinned) - Number(a.pinned)) || (t(b) - t(a)));
}
export async function savePartnerNote(fields, id) {
  const q = id
    ? tbl('partner_note').update({ ...fields, updated_at: new Date().toISOString() }).eq('id', id)
    : tbl('partner_note').insert(fields);
  const { data, error } = await q.select().single();
  err(error);
  return data;
}
export async function deletePartnerNote(id) {
  const { error } = await tbl('partner_note').delete().eq('id', id);
  err(error);
}

// ── ไฟล์ของแบรนด์/การประชุม → bucket ส่วนตัว inbound-docs (เปิดด้วย signed URL ผ่าน documentUrl) ──
// (ไม่ใช้ inbound-photos ที่เป็น public เพราะ price list/สัญญา ห้ามหลุด)
export async function uploadPartnerFile(partnerId, file) {
  file = await toUploadableImage(file);
  const path = `partners/${partnerId}/${Date.now()}_${safeName(file.name)}`;
  const { error: upErr } = await supabase.storage.from('inbound-docs').upload(path, file);
  err(upErr);
  return { id: 'f' + Date.now(), path, name: file.name, mime: file.type };
}

// ── เตือนความจำของแบรนด์ (ตาราง reminder เดียวกับแท็บ "วันนี้") ──
export async function listPartnerReminders(partnerId) {
  return rows(tbl('reminder').select('*').eq('partner_id', partnerId)
    .order('done', { ascending: true })
    .order('due_date', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: false }));
}

// ── เอกสารจากตู้ทั้งหมดของแบรนด์ (PI / Invoice / Packing List / BL / หลักฐานโอน …) — ไม่ต้องอัปซ้ำ ──
// เอกสารระดับ consignment ของเจ้านี้ + เอกสารระดับตู้ (consignment_id null) ของตู้ที่มีเจ้านี้
export async function partnerDocuments(shipmentIds, consignmentIds) {
  if (!shipmentIds?.length) return [];
  const docs = await rows(tbl('document').select('*').in('shipment_id', shipmentIds)
    .order('uploaded_at', { ascending: false }));
  const cset = new Set(consignmentIds ?? []);
  return docs.filter(d => !d.consignment_id || cset.has(d.consignment_id));
}

// ZIP เอกสารทั้งหมดของแบรนด์ (โฟลเดอร์ต่อตู้)
export async function buildPartnerPacket(partner, docs, shipCodeById = {}) {
  if (!docs?.length) throw new Error('ยังไม่มีเอกสารจากตู้ของแบรนด์นี้');
  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  const used = new Set();
  for (const d of docs) {
    try {
      const url = await documentUrl(d.file_path);
      const blob = await (await fetch(url)).blob();
      const folder = (shipCodeById[d.shipment_id] || 'ตู้').replace(/[\\/:*?"<>|]/g, '-');
      const base = `${folder}/${(d.req_type || 'doc')}_${(d.file_name || 'file').replace(/[\\/:*?"<>|]/g, '-')}`;
      let n = base, i = 1;
      while (used.has(n)) n = base.replace(/(\.[^.]+)?$/, `_${i++}$1`);
      used.add(n);
      zip.file(n, blob);
    } catch { /* ข้ามไฟล์ที่โหลดไม่ได้ */ }
  }
  const out = await zip.generateAsync({ type: 'blob' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(out);
  a.download = `${(partner.supplier_name || 'brand').replace(/[\\/:*?"<>|]/g, '-')}_เอกสาร.zip`;
  a.click();
  URL.revokeObjectURL(a.href);
  return docs.length;
}

// จำนวนสินค้าในคลังต่อชื่อแบรนด์ (v_classification_brands) → map ชื่อ normalize → n
export async function partnerProductCounts() {
  const list = await rows(tbl('v_classification_brands').select('*')).catch(() => []);
  const m = {};
  for (const r of list) if (r.brand) m[normName(r.brand)] = (m[normName(r.brand)] || 0) + Number(r.n || 0);
  return m;
}

// สร้างแบรนด์อัตโนมัติจากข้อมูลที่มีอยู่แล้ว (supplier ในตู้ + แบรนด์สินค้าในคลัง) — ไม่ต้องพิมพ์ซ้ำ
// กติกา: supplier_name ในตู้ = แบรนด์หลัก · แบรนด์สินค้าที่ "ขึ้นต้นด้วย" ชื่อแบรนด์ที่มีอยู่ (เช่น Snow Peak Festival)
// = ชื่อสะกดอื่นของแบรนด์นั้น · แบรนด์สินค้าอื่นที่ไม่ตรงใคร = แบรนด์ใหม่
export async function seedPartnersFromData(existing = []) {
  const [ships, brands] = await Promise.all([listShipments(), classificationBrands().catch(() => [])]);
  const partners = existing.map(p => ({ ...p, aliases: [...(p.aliases ?? [])] }));
  const created = [], updated = new Set();
  const find = (name) => partners.find(p => partnerMatches(p, name));
  const supplierNames = [...new Set(ships.flatMap(s => (s.consignment ?? []).map(c => (c.supplier_name || '').trim()).filter(Boolean)))];
  for (const name of supplierNames) {
    if (find(name)) continue;
    // ชื่อสะกดอื่นของเจ้าที่มีอยู่? (ต่างแค่ตัวพิมพ์/ช่องว่าง เทียบผ่าน normName แล้วข้างบน) → ใหม่
    const p = { supplier_name: name, aliases: [], brand_hue: null, _new: true };
    partners.push(p); created.push(p);
  }
  const brandNames = brands.map(b => (b.brand || '').trim()).filter(Boolean);
  for (const bn of brandNames) {
    if (find(bn)) continue;
    const n = normName(bn);
    const parent = partners.find(p => partnerNames(p).some(a => n.startsWith(normName(a) + ' ')));
    if (parent) { parent.aliases.push(bn); if (!parent._new) updated.add(parent); continue; }
    const p = { supplier_name: bn, aliases: [], brand_hue: null, _new: true };
    partners.push(p); created.push(p);
  }
  let nNew = 0, nAlias = 0;
  for (const p of created) {
    const { _new, ...fields } = p;
    await savePartner(fields);
    nNew++;
  }
  for (const p of updated) {
    const { _new, ...fields } = p;
    await savePartner({ aliases: fields.aliases }, p.id);
    nAlias++;
  }
  return { created: nNew, aliased: nAlias };
}

// ══════════════ ใบสั่งซื้อ ↔ ของที่มาถึง (deploy-28) ══════════════
// ปัญหาจริง: supplier รับออเดอร์ปีที่แล้ว มาส่งปีนี้ — ไม่มีใครจำได้ว่าสั่งกี่ชิ้น ราคาที่ตกลงเท่าไร
// วิธีแก้: purchase_order/po_line = "ของที่สั่ง" อยู่ถาวรกับแบรนด์ (ไม่ผูกตู้)
//          line_item (ของเดิม บน consignment) = "ของที่มาถึง" → ชี้กลับด้วย po_line_id
// ⚠ เทียบราคาที่ "ต่อชิ้น" เสมอ (pack_size) — PI บางเจ้าให้ราคาต่อกล่อง เช่น Nalgene 1 กล่อง = 6 ขวด
export const normSku = (s) => (s || '').normalize('NFC').trim().toUpperCase().replace(/[\s._-]+/g, '');

export async function listPurchaseOrders(partnerId) {
  if (!partnerId) return [];
  const [heads, sums] = await Promise.all([
    rows(tbl('purchase_order').select('*').eq('partner_id', partnerId)
      .order('order_date', { ascending: false, nullsFirst: false }).order('created_at', { ascending: false })),
    rows(tbl('v_po_summary').select('*').eq('partner_id', partnerId)),
  ]);
  const byId = idx(sums, 'po_id');
  for (const h of heads) h.sum = byId[h.id] ?? null;
  return heads;
}

export async function poLineStatus(partnerId) {
  if (!partnerId) return [];
  return rows(tbl('v_po_line_status').select('*').eq('partner_id', partnerId)
    .order('sort_order', { ascending: true }));
}

export async function savePurchaseOrder(fields, id) {
  const q = id ? tbl('purchase_order').update({ ...fields, updated_at: new Date().toISOString() }).eq('id', id)
               : tbl('purchase_order').insert(fields);
  const { data, error } = await q.select().single();
  err(error);
  return data;
}
export async function deletePurchaseOrder(id) {
  const { error } = await tbl('purchase_order').delete().eq('id', id);   // po_line ตามไปด้วย (cascade)
  err(error);
}

// บรรทัดที่สั่ง — บันทึกทั้งใบทีเดียว (เพิ่ม/แก้/ลบ) ให้เหมือนกรอกในตาราง
export async function savePoLines(poId, rows_) {
  const existing = await rows(tbl('po_line').select('id').eq('po_id', poId));
  const keep = new Set(rows_.map(r => r.id).filter(Boolean));
  const gone = existing.filter(e => !keep.has(e.id)).map(e => e.id);
  if (gone.length) { const { error } = await tbl('po_line').delete().in('id', gone); err(error); }
  const mk = (r, i) => ({
    sku: (r.sku || '').trim() || null,
    description: (r.description || '').trim() || null,
    qty: Number(r.qty) || 0,
    unit: (r.unit || '').trim() || null,
    pack_size: Math.max(1, Math.round(Number(r.pack_size) || 1)),
    unit_price: r.unit_price === '' || r.unit_price == null ? null : Number(r.unit_price),
    note: (r.note || '').trim() || null,
    sort_order: i,
  });
  const news = [], upds = [];
  rows_.forEach((r, i) => (r.id ? upds.push({ id: r.id, ...mk(r, i) }) : news.push({ po_id: poId, ...mk(r, i) })));
  if (news.length) { const { error } = await tbl('po_line').insert(news); err(error); }
  for (const u of upds) {
    const { id, ...f } = u;
    const { error } = await tbl('po_line').update({ ...f, updated_at: new Date().toISOString() }).eq('id', id);
    err(error);
  }
  return { added: news.length, updated: upds.length, removed: gone.length };
}

// ราคาที่เคยตกลง/เคยได้ ต่อชิ้น — ตอบคำถาม "ราคานี้จริงรึป่าว"
export async function pricePoints(partnerId) {
  if (!partnerId) return [];
  return rows(tbl('v_price_point').select('*').eq('partner_id', partnerId)
    .order('on_date', { ascending: false, nullsFirst: false }));
}

// จับคู่ของที่มาถึง → บรรทัดที่สั่ง ด้วย SKU (ใบเก่าก่อน) แล้วเกลี่ยตามจำนวนที่ยังไม่มา
export async function matchPoLines(partnerId, consignmentIds) {
  if (!partnerId || !consignmentIds?.length) return { linked: 0, left: 0 };
  const pos = await rows(tbl('purchase_order').select('id, order_date')
    .eq('partner_id', partnerId).neq('status', 'cancelled'));
  if (!pos.length) return { linked: 0, left: 0 };
  const poIds = pos.map(p => p.id);
  const [plines, recv] = await Promise.all([
    rows(tbl('po_line').select('id, po_id, sku, pieces').in('po_id', poIds)),
    rows(tbl('line_item').select('id, sku, po_ref, qty, pack_size, po_line_id, consignment_id')
      .in('consignment_id', consignmentIds)),
  ]);
  if (!plines.length) return { linked: 0, left: 0 };
  const seq = Object.fromEntries(pos.map(p => [p.id, p.order_date || '9999-12-31']));
  const left = {};                                     // ยังไม่มาอีกกี่ชิ้นต่อบรรทัดที่สั่ง
  for (const l of plines) left[l.id] = Number(l.pieces) || 0;
  for (const li of recv) if (li.po_line_id && left[li.po_line_id] != null)
    left[li.po_line_id] -= (Number(li.qty) || 0) * (Number(li.pack_size) || 1);

  const bySku = {};
  for (const l of plines) {
    const k = normSku(l.sku); if (!k) continue;
    (bySku[k] ??= []).push(l);
  }
  for (const k in bySku) bySku[k].sort((a, b) => String(seq[a.po_id]).localeCompare(String(seq[b.po_id])));

  let linked = 0;
  for (const li of recv) {
    if (li.po_line_id) continue;
    const raw = (li.sku || li.po_ref || '').trim();
    const cand = bySku[normSku(raw)];
    if (!cand?.length) continue;
    const pick = cand.find(l => left[l.id] > 0) ?? cand[0];   // เต็มทุกใบแล้ว = เกาะใบแรก (ขึ้นธง "มาเกิน")
    const { error } = await tbl('line_item')
      .update({ po_line_id: pick.id, sku: li.sku || raw || null }).eq('id', li.id);
    err(error);
    left[pick.id] -= (Number(li.qty) || 0) * (Number(li.pack_size) || 1);
    linked++;
  }
  const unmatched = recv.filter(li => !li.po_line_id && !bySku[normSku(li.sku || li.po_ref || '')]).length;
  return { linked, left: unmatched };
}

// ผูก/ปลดด้วยมือ (กรณี SKU ไม่ตรงกันระหว่างใบสั่งกับ invoice)
export async function setLinePoLink(lineItemId, poLineId) {
  const { error } = await tbl('line_item').update({ po_line_id: poLineId }).eq('id', lineItemId);
  err(error);
}
// ราคา/กล่อง ของบรรทัดที่มาถึง (เติมทีหลังได้ ตอนได้ invoice ตัวจริง)
export async function setLinePrice(lineItemId, { unit_price, pack_size }) {
  const f = {};
  if (unit_price !== undefined) f.unit_price = unit_price === '' || unit_price == null ? null : Number(unit_price);
  if (pack_size !== undefined) f.pack_size = Math.max(1, Math.round(Number(pack_size) || 1));
  const { error } = await tbl('line_item').update(f).eq('id', lineItemId);
  err(error);
}

// บรรทัดที่มาถึงของตู้ (ใช้เทียบในหน้าแบรนด์)
export async function receivedLines(consignmentIds) {
  if (!consignmentIds?.length) return [];
  return rows(tbl('line_item')
    .select('id, consignment_id, sku, po_ref, description, qty, unit, pack_size, unit_price, po_line_id')
    .in('consignment_id', consignmentIds));
}

// สถิติแอดมิน: ใบสั่งซื้อค้าง / ของที่ยังไม่มา / จำนวนกับราคาที่ไม่ตรง
export async function poStats() {
  const [sums, parts] = await Promise.all([
    rows(tbl('v_po_summary').select('*')),
    rows(tbl('supplier_profile').select('id, supplier_name')),
  ]);
  const name = Object.fromEntries(parts.map(p => [p.id, p.supplier_name]));
  for (const s of sums) s.brand = name[s.partner_id] ?? '—';
  return sums;
}

// ══════════════ จัดการสิทธิ์ผู้ใช้ (deploy-29) — แอดมินเท่านั้น ══════════════
// ก่อน deploy-29: ไม่มีแถวใน inbound.app_role ก็ยังเข้าแอพได้ เพราะ user_role() fallback
// ไปอ่าน public.profiles (บัญชีใหม่ของแอพอื่น = ได้ warehouse ฟรี) — ตอนนี้ตัดออกแล้ว
// ไม่มีแถว = ไม่มีสิทธิ์ ทุกคนต้องถูกเพิ่มที่หน้านี้
export const APP_ROLES = [
  { k: 'admin',       t: 'แอดมิน',           d: 'เห็นทุกอย่าง · ดูสถิติ · จัดการสิทธิ์และกฎพิกัด · ลบแบรนด์ได้' },
  { k: 'coordinator', t: 'ผู้ประสานงานนำเข้า', d: 'งานประจำวันทั้งหมด · เห็นราคาและใบสั่งซื้อ (ไม่เห็นสถิติ)' },
  { k: 'accountant',  t: 'บัญชี',             d: 'เห็นราคาและใบสั่งซื้อ เหมือนผู้ประสานงาน' },
  { k: 'warehouse',   t: 'คลัง',              d: 'ดูตู้และเอกสารได้ · ไม่เห็นราคา ต้นทุน และใบสั่งซื้อ' },
];
export const roleLabel = (k) => APP_ROLES.find(r => r.k === k)?.t ?? (k || 'ยังไม่มีสิทธิ์');

// deploy-32: ผ่าน RPC (SECURITY DEFINER + ด่าน is_admin ข้างใน) แทน view ที่ Security Advisor ฟ้อง auth_users_exposed
// เรียงมาจาก SQL แล้ว (คนมีสิทธิ์ขึ้นก่อน) · ไม่ใช่แอดมิน = error 42501
export async function listAppUsers() {
  return rows(supabase.rpc(rpcName('list_users')));
}

export async function setAppRole(userId, role, note) {
  if (!userId) throw new Error('ไม่มี user id');
  if (!APP_ROLES.some(r => r.k === role)) throw new Error(`สิทธิ์ไม่ถูกต้อง: ${role}`);
  // ⚠ ห้ามใช้ upsert ผ่าน bridge: view ไม่มี unique constraint ให้ ON CONFLICT เกาะ
  //   (Postgres ฟ้อง "no unique or exclusion constraint matching the ON CONFLICT specification")
  const patch = { role, note: (note || '').trim() || null };
  const { data: upd, error: e1 } = await tbl('app_role').update(patch).eq('user_id', userId).select('user_id');
  err(e1);
  if (!upd?.length) {
    const { error: e2 } = await tbl('app_role').insert({ user_id: userId, ...patch });
    err(e2);
  }
}

// ถอดสิทธิ์ = ลบแถว (เข้าแอพนี้ไม่ได้ทันที) — บัญชี Supabase และแอพอื่นไม่กระทบ
export async function removeAppRole(userId) {
  const { error } = await tbl('app_role').delete().eq('user_id', userId);
  err(error);
}

// ══════════════ อ่าน invoice แล้วจับคู่ทะเบียนสินค้า (25 ต.ค. 2569) ══════════════
// user: "ถ้ามี upload ไฟล์ invoice แล้วแอพสร้างใบรายละเอียดสินค้าให้เอง
//        โดยแจ้งให้เพิ่มเฉพาะสินค้าที่ยังไม่เคยมี หรือต้องการการแก้ไข"
// ⇒ สองตัวช่วยนี้ให้หน้ายืนยัน (ImportInvoiceModal) แยกของเป็น 3 กองได้

// รหัสจากไฟล์ → ใบข้อมูลสินค้าที่มีอยู่ (คีย์เป็น normSku: ตัดช่องว่าง/จุด/ขีด + ตัวใหญ่)
// ดึงทะเบียนมาทั้งก้อนครั้งเดียวแล้วเทียบในเครื่อง เพราะ .in() ของ PostgREST เทียบตรงตัวอักษร
// ซึ่งพลาดทุกครั้งที่ไฟล์เขียนรหัสคนละรูป ('AC 24SU10500CH' vs 'AC-24SU10500CH')
export async function matchProductCards(codes) {
  const want = new Set((codes || []).map(normSku).filter(Boolean));
  if (!want.size) return {};
  const all = await rows(tbl('product_classification')
    .select('id, sku, brand, name_th, name_en, what_is_it, material, usage_desc, origin_country, power_spec, hs_code, hs_confirmed, photos'));
  const out = {};
  for (const c of all) {
    const k = normSku(c.sku);
    if (k && want.has(k)) out[k] = c;
  }
  return out;
}

// เติมช่องที่ว่างจาก "ทะเบียนสินค้ากลาง" (PD) ให้ใบข้อมูลสินค้าหลายใบพร้อมกัน
// ⚠ เติมเฉพาะช่องที่ **ว่างอยู่** — ไม่ทับของที่คนกรอก/ใส่รูปไว้เอง
// ⚠ PD ให้ได้แค่ รูป · หมวด · แบรนด์ (ชื่อไทยใน PD มักเป็นชื่ออังกฤษ จึงไม่เอามาทับ)
//   **วัสดุ · คืออะไร · ใช้ทำอะไร PD ไม่มี** และห้ามเดา — เป็นช่องที่ตัดสินบรรทัดภาษี
// ดึงทีละก้อนด้วย .in() บนรหัสจริง (ไม่ใช้ regexp ฝั่ง DB — ช้ามากบนตาราง 14,000 แถว)
export async function fillFromProductMaster(cards) {
  const list = (cards || []).filter(c => c?.sku);
  if (!list.length) return { checked: 0, found: 0, photo: 0, category: 0, brand: 0 };

  const CHUNK = 200;
  const pd = {};
  for (let i = 0; i < list.length; i += CHUNK) {
    const skus = list.slice(i, i + CHUNK).map(c => c.sku.trim());
    const got = await rows(tbl('pd_products').select(PD_COLS).in('sku', skus));
    for (const r of got) if (r.sku) pd[normSku(r.sku)] = r;
  }

  let photo = 0, category = 0, brand = 0, found = 0;
  for (const c of list) {
    const m = pd[normSku(c.sku)];
    if (!m) continue;
    found++;
    const patch = {};
    if (!(c.photos ?? []).length && m.image_url) {
      patch.photos = [{ url: m.image_url, caption: 'PD' }]; photo++;
    }
    if (!(c.category ?? '').trim() && (m.category ?? '').trim()) { patch.category = m.category; category++; }
    if (!(c.brand ?? '').trim() && (m.brand_label ?? m.brand ?? '').trim()) {
      patch.brand = m.brand_label ?? m.brand; brand++;
    }
    if (!Object.keys(patch).length) continue;
    const { error } = await tbl('product_classification').update(patch).eq('id', c.id);
    err(error);
  }
  return { checked: list.length, found, photo, category, brand };
}

// ตัดหางที่ระบบเติมเองออกจากชื่อ ก่อนเอาไปใช้เป็นชื่อจริง
export const cleanImportedName = (s) =>
  (s || '').replace(/\s*—\s*SKU\s+\S+\s*ไม่พบในคลัง\s*$/u, '').trim();

// สร้างใบข้อมูลสินค้าแบบ "โครง" ให้รายการที่ยังไม่เคยมีในทะเบียน
// ⚠ ใส่เฉพาะสิ่งที่ **เอกสารบอกจริง** เท่านั้น — รหัส · ชื่อจาก invoice · แบรนด์ของใบนั้น
//   **ไม่เดาวัสดุ ไม่เดาคำบรรยาย ไม่เดาพิกัด ไม่เดาประเทศผลิต**
//   (วัสดุเป็นตัวตัดสินบรรทัดภาษี เดาผิด = สำแดงผิด · ปล่อยว่างให้คนเติมพร้อมหลักฐาน)
// 🚨 ประเทศผลิต: เดิมก๊อป "ประเทศต้นทางของ supplier" ลงทุกใบ ⇒ ของจีน/เวียดนามที่ส่งจากญี่ปุ่นกลายเป็น JP
//   51 ใบในตู้ SP-261015 แล้วระบบขอ Form JTEPA ทั้งที่ใช้สิทธิ์ไม่ได้ (28 ต.ค. 69)
//   user 29 ต.ค.: "การ์ดใหม่ควรใช้แหล่งกำเนิดจากข้อมูลจริง" ⇒ เว้นว่าง แล้วเติมจาก packing list (OriginFromFile)
//   หรือดูจากฉลากสินค้า — ประเทศที่ส่งของมา ≠ ประเทศที่ผลิต
// คืน map รหัส(normSku) → ใบที่สร้าง เพื่อเอาไปผูกกับบรรทัดในตู้
export async function createStubCards(lines, { brand } = {}) {
  const seen = new Set();
  const payload = [];
  for (const l of lines || []) {
    const k = normSku(l.sku);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    payload.push({
      sku: (l.sku || '').trim(),
      // ⚠ importInvoiceLines ต่อหาง "— SKU xxx ไม่พบในคลัง" ให้บรรทัดที่จับคู่ไม่ได้
      //   ถ้าก๊อปดิบ ๆ ชื่อบนเอกสารที่ส่ง broker จะมีคำว่า "ไม่พบในคลัง" ติดไปด้วย (เกิดจริง 25 ต.ค. 69)
      name_en: cleanImportedName(l.description) || null,
      brand: brand || null,
    });
  }
  if (!payload.length) return {};
  const { data, error } = await tbl('product_classification').insert(payload).select('id, sku');
  err(error);
  const out = {};
  for (const c of data || []) out[normSku(c.sku)] = c;
  return out;
}

// ผูกบรรทัดในตู้เข้ากับใบข้อมูลสินค้าที่เพิ่งสร้าง (บรรทัดที่ classification_id ยังว่าง)
export async function linkLinesToCards(consignmentId, bySku) {
  const ids = Object.entries(bySku || {});
  if (!ids.length) return 0;
  const lines = await rows(tbl('line_item').select('id, sku').eq('consignment_id', consignmentId).is('classification_id', null));
  let n = 0;
  for (const li of lines) {
    const card = bySku[normSku(li.sku)];
    if (!card) continue;
    const { error } = await tbl('line_item').update({ classification_id: card.id }).eq('id', li.id);
    err(error);
    n++;
  }
  return n;
}

// เขียนประเทศผลิตลงใบข้อมูลสินค้าหลายใบ (จาก packing list — components/OriginFromFile.jsx)
//   changes = [{ id, from, to }] · เขียนเฉพาะใบที่ประเทศ "ยังเป็นค่าเดิมที่โชว์ให้คนดู" (from)
//   → มีคนแก้ใบนั้นระหว่างที่หน้าต่างเปิดอยู่ = ไม่ทับ แล้วนับเป็น "ข้าม"
//   ⚠ ใบข้อมูลสินค้าใช้ร่วมทุกตู้ — เปลี่ยนตรงนี้ = ตู้อื่นที่มีรหัสเดียวกันเห็นค่าใหม่ด้วย
//   ไม่โยนกลางทาง: ใบที่พลาดนับเป็น failed แล้วทำใบถัดไป — คนต้องรู้ว่าบันทึกไปแล้วกี่ใบ (รีวิว 29 ต.ค. 69)
export async function setCardOrigins(changes) {
  let done = 0, error = null;
  const written = [], skipped = [], failed = [];
  for (const c of changes || []) {
    try {
      let q = tbl('product_classification').update({ origin_country: c.to }).eq('id', c.id);
      q = c.from ? q.eq('origin_country', c.from) : q.is('origin_country', null);
      const { data, error: e } = await q.select('id');
      if (e) throw e;
      if (data?.length) { done++; written.push(c); } else skipped.push(c);
    } catch (e) { failed.push(c); error = error ?? (e?.message ?? String(e)); }
  }
  return { done, written, skipped, failed, error };
}

// ช่องที่ยังขาดของใบข้อมูลสินค้าหนึ่งใบ — ใช้กติกาเดียวกับด่าน "ครบพร้อมส่ง broker" ใน Dossier.jsx
// ⚠ ถ้าแก้กติกาที่นี่ ต้องแก้ที่ Dossier.jsx ด้วย ไม่งั้นหน้ายืนยันจะบอกว่าครบ แต่หน้าพิมพ์บอกว่าขาด
// (power_spec ตรวจไม่ได้ที่นี่ เพราะต้องรู้ว่าสินค้าเป็นเครื่องใช้ไฟฟ้าไหม ซึ่งดูจากบรรทัดในตู้)
export function cardGaps(card) {
  if (!card) return ['ยังไม่มีใบข้อมูลสินค้า'];
  const gaps = [];
  if (!(card.photos ?? []).length) gaps.push('รูป');
  // ชื่อไทยต้องเป็นภาษาไทยจริง — ช่องที่ใส่ชื่ออังกฤษไว้ = ยังไม่มี (ศุลกากรใช้ช่องนี้ · user 29 ต.ค. 69)
  if (!hasThai(card.name_th)) gaps.push('ชื่อไทย');
  if (!card.what_is_it) gaps.push('คืออะไร');
  if (!hasMaterial(card)) gaps.push('วัสดุ');
  if (!card.usage_desc) gaps.push('ใช้ทำอะไร');
  if (!card.origin_country) gaps.push('ประเทศต้นกำเนิด');
  if (!card.brand) gaps.push('แบรนด์');
  return gaps;
}

// ชื่อไฟล์เอกสารทุกใบ (เบา — เอาไว้ให้ค้นหาตู้ด้วยเลขที่เขียนอยู่บนชื่อไฟล์ เช่น เลข invoice ของ forwarder)
export async function allDocNames() {
  return rows(tbl('document').select('shipment_id, file_name, req_type'));
}

// ══════════════ จัดการบัญชี: รหัสผ่าน · อีเมลเข้าระบบ · ประวัติ (deploy-35) ══════════════
// งานพวกนี้ต้องใช้กุญแจ service-role จึงทำผ่าน Cloudflare Function เท่านั้น
// หน้าเว็บส่งแค่โทเคนของคนที่ล็อกอินอยู่ไปให้ตรวจ — ไม่เคยเห็นกุญแจ

export const MIN_PW = 8;   // ใช้ร่วมกันทั้ง NewUserModal · ตั้งรหัสใหม่ · หน้าตั้งรหัสของตัวเอง

async function callUsersApi(method, body) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('เซสชันหมดอายุ — เข้าสู่ระบบใหม่อีกครั้ง');
  const r = await fetch('/api/users', {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  let out = null;
  try { out = await r.json(); } catch { /* ไม่ใช่ JSON = ฟังก์ชันยังไม่ถูก deploy */ }
  if (!r.ok || !out?.ok) {
    const e = new Error(out?.error
      ?? (r.status === 404
        ? 'ยังไม่มี /api/users บนเซิร์ฟเวอร์ — ต้อง deploy ใหม่พร้อมโฟลเดอร์ functions/'
        : `ทำรายการไม่สำเร็จ (${r.status})`));
    e.notConfigured = !!out?.not_configured;   // ยังไม่ได้ตั้งกุญแจ — คนละเรื่องกับไม่มีสิทธิ์
    e.setup = out?.setup; e.where = out?.where;
    throw e;
  }
  return out;
}

// ถามตั้งแต่ตอนโหลดหน้า ว่าฝั่งเซิร์ฟเวอร์พร้อมหรือยัง — จะได้บอกล่วงหน้า
// ไม่ใช่ให้แอดมินพิมพ์รหัสผ่านเสร็จแล้วค่อยเจอว่าทำไม่ได้
export async function serverStatus() {
  try { return await callUsersApi('GET'); }
  catch (e) { return { ok: false, configured: false, error: e.message, setup: e.setup, where: e.where }; }
}

export const createAppUser = ({ email, password, fullName, role }) =>
  callUsersApi('POST', { email, password, full_name: fullName, role });

export const resetUserPassword = (userId, password) =>
  callUsersApi('PATCH', { user_id: userId, action: 'reset_password', password });

export const setUserEmail = (userId, email) =>
  callUsersApi('PATCH', { user_id: userId, action: 'set_email', email });

export const ADMIN_ACTION_LABEL = {
  create_user: 'สร้างบัญชีใหม่',
  set_role: 'เปลี่ยนสิทธิ์',
  remove_role: 'ถอดสิทธิ์',
  reset_password: 'ตั้งรหัสผ่านใหม่',
  set_email: 'เปลี่ยนอีเมลเข้าระบบ',
  disable_login: 'ปิดบัญชี',
  enable_login: 'เปิดบัญชีคืน',
  delete_user: 'ลบบัญชี',
};

export async function listAdminEvents(limit = 200) {
  return rows(supabase.rpc(rpcName('list_admin_events'), { p_limit: limit }));
}

// ══════════════ ถังขยะ (deploy-33) — ของที่ถูกลบ กู้คืนได้ · แอดมินเท่านั้น ══════════════
// RLS กันไว้แล้วที่ฐานข้อมูล (p_trash_admin) — คนอื่นเรียกได้แต่จะได้ 0 แถว
export const TRASH_LABEL = {
  shipment: 'ตู้', consignment: 'ของจาก supplier', line_item: 'รายการสินค้าในตู้',
  document: 'ไฟล์เอกสาร', document_requirement: 'ช่องเอกสาร', certificate_of_origin: 'Form/CO',
  product_classification: 'สินค้าในคลัง', material_option: 'ตัวเลือกวัสดุ',
  supplier_profile: 'แบรนด์/คู่ค้า', partner_contact: 'ผู้ติดต่อ', partner_note: 'ประชุม/บันทึก',
  purchase_order: 'ใบสั่งซื้อ', po_line: 'บรรทัดในใบสั่งซื้อ', reminder: 'งานที่ต้องทำ',
  shipment_event: 'บันทึกในสมุดตู้', app_role: 'สิทธิ์ผู้ใช้', signer: 'ชื่อผู้จัดทำ',
  hs_rule: 'กฎพิกัด', permit: 'ใบอนุญาต', payment: 'การจ่ายเงิน', declaration: 'ใบขนสินค้า',
  product_file: 'ไฟล์ของสินค้า',
  material_research: 'งานหาวัสดุด้วย AI', brand_material_source: 'ทะเบียนเว็บแบรนด์ (หาวัสดุ)', material_ai_control: 'ตั้งค่าคิวหาวัสดุ',
};
// บรรยายแถวที่ถูกลบให้คนอ่านรู้ว่า "ของชิ้นไหน" โดยไม่ต้องรู้โครงตาราง
export function describeTrashRow(t) {
  const d = t.row_data ?? {};
  if (t.op === 'unlink') return `ความเชื่อมโยง ${d.column} ที่ถูกตัดออก`;
  const pick = (...keys) => keys.map(k => d[k]).find(v => v != null && String(v).trim() !== '');
  const main = pick('code', 'file_name', 'supplier_name', 'name', 'full_name', 'title',
                    'description', 'sku', 'po_no', 'hs_code', 'invoice_no', 'req_type', 'event_type', 'model_code', 'brand');
  const extra = [d.qty != null ? `${Number(d.qty).toLocaleString()} ${d.unit ?? ''}`.trim() : null,
                 d.invoice_no && d.invoice_no !== main ? `INV ${d.invoice_no}` : null,
                 d.role, d.sku && d.sku !== main ? d.sku : null,
                 d.model_code && d.brand ? d.brand : null].filter(Boolean);
  return [main ?? '(ไม่มีชื่อ)', extra.length ? `· ${extra.join(' · ')}` : ''].join(' ').trim();
}

export async function listTrash(limit = 400) {
  return rows(tbl('trash').select('*').order('id', { ascending: false }).limit(limit));
}
export async function restoreTrash(id) {
  const { data, error } = await supabase.rpc(rpcName('restore_trash'), { p_id: id });
  err(error);
  return data;
}
// กู้ทั้งก้อน — ลบครั้งเดียวมักลากของไปหลายสิบแถว ต้องเอากลับพร้อมกันถึงจะผูกกันติด
export async function restoreTrashBatch(txid) {
  const { data, error } = await supabase.rpc(rpcName('restore_trash_batch'), { p_txid: txid });
  err(error);
  return data;
}

// ══════════════ หาวัสดุด้วย AI (deploy-38 · สัญญาอยู่ที่ docs/ai-materials-plan.md) ══════════════
// ลงคิวผ่าน edge function inbound-materials · ยืนยัน/ไม่ใช้/ใช้กับ SKU เพิ่ม ผ่าน RPC (ib_*) · อ่านผ่าน view (ib_*)
// ⚠ ทุกฟังก์ชันโยน Error ที่ข้อความเป็นภาษาไทยเสมอ — หน้าเว็บโชว์ e.message ได้เลย ไม่มีข้อความดิบถึงพนักงาน
//   e.notReady = ยังไม่รัน deploy-38 / ยังไม่ deploy ฟังก์ชัน (หน้าเว็บขึ้นก่อน SQL ตามลำดับขึ้นจริง) → ซ่อนปุ่ม / ขึ้น "ยังไม่พร้อมใช้งาน"
const MAT_MISSING = new Set(['PGRST205', 'PGRST202', 'PGRST204', '42P01', '42883', '42703']);
export const materialsNotReady = (e) => !!e?.notReady || MAT_MISSING.has(e?.code)
  || /material_research|material_ai_control|Could not find the (table|function)/i.test(e?.message ?? '');

const MAT_NOT_READY = 'ระบบหาวัสดุด้วย AI ยังไม่พร้อมใช้งาน — ฝากแจ้งแอดมินให้ช่วยตั้งค่าให้เสร็จก่อน';
export const MAT_UNREACHABLE = 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ — ฝากตรวจอินเทอร์เน็ตแล้วลองใหม่อีกครั้ง';
const matThaiError = (msg, extra = {}) => Object.assign(new Error(msg), extra);
function materialsError(e, fallback) {
  if (materialsNotReady(e)) return matThaiError(MAT_NOT_READY, { notReady: true });
  const msg = String(e?.message ?? '');
  // ข้อความที่ฐานข้อมูลตั้งใจบอกคน (RAISE ภาษาไทย) ใช้ได้เลย แค่เอา uuid ออก
  if (hasThai(msg)) return matThaiError(msg.replace(/\s*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '').trim());
  if (e?.code === '42501') return matThaiError('บัญชีนี้ทำรายการนี้ไม่ได้ — เปิดให้เฉพาะแอดมินและผู้ประสานงาน');
  if (/fetch|network|load failed/i.test(msg)) return matThaiError(MAT_UNREACHABLE);
  return matThaiError(fallback);
}

export async function callMaterials(body) {
  const { data: { session } } = await supabase.auth.getSession();
  // ไม่มี session แล้วเรียกไปเลย = anon key ถูกส่งเป็น Bearer → 401 ที่บอกอะไรคนไม่ได้
  if (!session?.access_token) throw matThaiError('เซสชันหมดอายุ — ขอให้ช่วยเข้าสู่ระบบใหม่อีกครั้ง');
  const { data, error } = await supabase.functions.invoke('inbound-materials', { body });
  if (!error) {
    if (data == null || typeof data !== 'object') throw matThaiError('ระบบหาวัสดุตอบกลับมาไม่ครบ — ฝากลองใหม่อีกครั้ง');
    return data;
  }
  // FunctionsFetchError = ส่งไม่ถึง (ยังไม่ deploy → preflight ไม่ผ่าน หรือเน็ตมีปัญหา — แยกจากฝั่งเบราว์เซอร์ไม่ได้)
  //   fetchFailed: ถ้าฟังก์ชันเพิ่งตอบได้ (ดูก่อนลงคิวผ่านแล้ว) ต้นเหตุคือเน็ต ผู้เรียกต้องบอกว่าเชื่อมต่อไม่ได้ ไม่ใช่ "ยังตั้งค่าไม่เสร็จ"
  if (error.name === 'FunctionsFetchError') throw matThaiError(MAT_NOT_READY, { notReady: true, fetchFailed: true });
  const status = error.context?.status;
  let j = null;
  try { j = await error.context?.json?.(); } catch { /* ไม่ใช่ JSON */ }
  if (status === 404) throw matThaiError(MAT_NOT_READY, { notReady: true });
  if (status === 401) throw matThaiError('เซสชันหมดอายุ — ขอให้ช่วยเข้าสู่ระบบใหม่อีกครั้ง');
  if (status === 403) throw matThaiError('บัญชีนี้ใช้หาวัสดุด้วย AI ไม่ได้ — เปิดให้เฉพาะแอดมินและผู้ประสานงาน');
  if (j?.error === 'too_many') {
    throw matThaiError(`ครั้งนี้มีรุ่นที่ต้องค้นใหม่ ${j.models_new} รุ่น เกินเพดาน ${j.per_click_max} รุ่นต่อครั้ง — ฝากช่วยเลือกให้น้อยลงแล้วลองใหม่`,
      { tooMany: { models_new: j.models_new, per_click_max: j.per_click_max } });
  }
  if (hasThai(j?.error)) throw matThaiError(String(j.error));
  throw matThaiError(status >= 500 ? 'ระบบหาวัสดุขัดข้องชั่วคราว — ฝากลองใหม่อีกครั้ง' : 'ส่งคำขอหาวัสดุไม่สำเร็จ — ฝากลองใหม่อีกครั้ง');
}

// ids = classification id · dryRun = ดูจำนวนรุ่น/ค่าใช้จ่ายก่อน ไม่เขียนอะไร ไม่เริ่มงาน · docDate = วันที่ invoice (AI ใช้เลือกซีซั่น)
export const enqueueMaterials = (ids, { force = false, dryRun = false, docDate = null } = {}) => callMaterials({
  action: 'enqueue',
  classification_ids: ids,
  ...(force ? { force: true } : {}),
  ...(dryRun ? { dry_run: true } : {}),
  ...(docDate ? { doc_date: docDate } : {}),
});

// เดินคิวเดี๋ยวนี้ — คำตอบบอก processed (จำนวนงานรอบนี้) · skipped:true = รอบนี้ระบบไม่รับงาน (worker เก่าเกิน / มีงานค้าง)
//   หน้านี้โหลดรายการผ่านแล้ว ส่งไม่ถึง = เชื่อมต่อไม่ได้ ไม่ใช่ยังตั้งค่าไม่เสร็จ
export async function runMaterialQueue() {
  try { return await callMaterials({ action: 'run' }); }
  catch (e) { throw e?.fetchFailed ? matThaiError(MAT_UNREACHABLE) : e; }
}

// ส่วนที่ยังไม่จบ = แสดงครบทุกแถว (งานค้างห้ามหายจากหน้าจอ: แถว draft ถือดัชนี active อยู่ ถ้ามองไม่เห็น รุ่นนั้นค้างถาวร)
// ส่วนที่จบแล้ว (ยืนยัน/ไม่ใช้) = เฉพาะล่าสุด MAT_DONE_CAP แถว
//   ⚠ PostgREST ตัดที่ max_rows เงียบ ๆ (ตอนนี้ 10,000 · เคยเป็น 1,000) → อ่านทีละหน้า หยุดเมื่อหน้าว่างเท่านั้น
//     ห้ามหยุดที่ "ได้น้อยกว่าขนาดหน้า" — ถ้าเพดานต่ำกว่าขนาดหน้า จะเข้าใจผิดว่าหมดแล้ว
//   ⚠ .order ต้องมี id ต่อท้าย: requested_at เท่ากันทั้งชุดได้ (ลงคิวพร้อมกัน) ลำดับไม่นิ่ง = ข้ามหน้าแล้วแถวหาย/ซ้ำ
const MAT_OPEN = ['queued', 'researching', 'researched', 'verifying', 'draft', 'not_found', 'error'];
const MAT_DONE = ['approved', 'rejected'];
export const MAT_DONE_CAP = 500;
const MAT_PAGE = 500;
async function matPages(statuses, cap = Infinity) {
  const seen = new Map();
  for (let from = 0; seen.size < cap;) {
    const { data, error } = await tbl('material_research').select('*').in('status', statuses)
      .order('requested_at', { ascending: false }).order('id', { ascending: true })
      .range(from, from + MAT_PAGE - 1);
    if (error) throw materialsError(error, 'โหลดรายการหาวัสดุไม่สำเร็จ — ฝากลองใหม่อีกครั้ง');
    if (!data?.length) break;
    const before = seen.size;
    for (const r of data) if (seen.size < cap) seen.set(r.id, r);
    if (seen.size === before) break;   // ไม่ได้แถวใหม่เลย (เซิร์ฟเวอร์ไม่เคารพ range) — กันวนไม่รู้จบ
    from += data.length;
  }
  return [...seen.values()];
}
export async function listMaterialResearch() {
  const [open, done] = await Promise.all([matPages(MAT_OPEN), matPages(MAT_DONE, MAT_DONE_CAP)]);
  return [...open, ...done].map(safeResearchRow).sort((a, b) => String(b.requested_at ?? '').localeCompare(String(a.requested_at ?? '')));
}

// แถวเดียว — ปุ่ม "ค้นใหม่" ในหน้าผลของหน้าต่างลงคิว ต้องรู้ SKU ทั้งรุ่น (คำตอบลงคิวบอกแค่ SKU ที่ยังไม่ได้ผล)
export async function getMaterialResearch(id) {
  const { data, error } = await tbl('material_research').select('*').eq('id', id).maybeSingle();
  if (error) throw materialsError(error, 'โหลดงานหาวัสดุไม่สำเร็จ — ฝากลองใหม่อีกครั้ง');
  return safeResearchRow(data);
}

// SKU ที่อยู่ในงาน AI ซึ่งบอกว่า "สีในรุ่นนี้วัสดุไม่เท่ากัน" — แผงเปลี่ยนตามจะไม่ติ๊กวัสดุให้ SKU พวกนี้
//   ยังไม่รัน deploy-38 = ยังไม่มีผล AI สักแถว = ว่าง · อ่านไม่ได้แบบอื่น = โยน (คนเรียกห้ามถือว่าไม่มี)
//   ⚠ ส่งเป็นสตริงที่ใส่ "…" เอง — overlaps() รับ array แล้ว join(',') เปล่า ๆ รหัสที่มี , หรือ " จะทำให้คำขอพัง/ค่าเพี้ยน
const pgTextArray = (list) => `{${list.map(s => `"${String(s).replace(/(["\\])/g, '\\$1')}"`).join(',')}}`;
export async function materialVariantSkus(skus) {
  const list = [...new Set((skus ?? []).filter(Boolean))];
  const out = new Set();
  if (!list.length) return out;
  try {
    for (const col of ['skus', 'late_skus']) {
      const got = await rows(tbl('material_research').select('skus, late_skus, result').overlaps(col, pgTextArray(list)));
      for (const r of got) if (variantsDiffer(r)) for (const s of [...(r.skus ?? []), ...(r.late_skus ?? [])]) out.add(s);
    }
  } catch (e) {
    // ดูรหัสเท่านั้น — materialsNotReady จับจากข้อความด้วย สิทธิ์ผิด ("permission denied for view ib_material_research") จะกลายเป็น "ไม่มีผล AI" แล้วติ๊กให้
    if (['PGRST205', 'PGRST202', '42P01'].includes(e?.code)) return new Set();
    throw e;
  }
  return out;
}

// ใบสินค้าตามรหัส — ค่าเดิมในหน้าตรวจผล + id สำหรับ ↻ ค้นใหม่
//   ⚠ เลือกเฉพาะคอลัมน์เดิม ไม่อ้าง 3 คอลัมน์ใหม่ของ deploy-38 — ใช้ได้ทั้งก่อนและหลังรัน SQL
export async function listCardsBySkus(skus) {
  const list = [...new Set((skus ?? []).filter(Boolean))];
  const out = [];
  try {
    for (let i = 0; i < list.length; i += 200) {
      const got = await rows(tbl('product_classification')
        .select('id, sku, brand, name_en, name_th, material, origin_country, hs_code').in('sku', list.slice(i, i + 200)));
      out.push(...got);
    }
  } catch (e) { throw materialsError(e, 'โหลดข้อมูลสินค้าไม่สำเร็จ — ฝากลองใหม่อีกครั้ง'); }
  return out;
}

const matRpc = async (name, args, fallback) => {
  const { data, error } = await supabase.rpc(rpcName(name), args);
  if (error) throw materialsError(error, fallback);
  return data;
};
// ⚠ ไม่ส่งคีย์ที่ไม่ได้ระบุ: ส่ง p_material_th แต่ไม่ส่ง en = ช่องอังกฤษถูกล้าง (ห้ามเอาค่า AI ไปประกบค่าที่คนแก้ — ดูสัญญา)
//   skus = ติ๊กเฉพาะ SKU ที่ตรงกับผลนี้ (รุ่นที่สีวัสดุต่างกัน)
export function approveMaterial(id, { materialTh, materialEn, skus } = {}) {
  return matRpc('approve_material_research', {
    p_id: id,
    ...(materialTh !== undefined ? { p_material_th: materialTh } : {}),
    ...(materialEn !== undefined ? { p_material_en: materialEn } : {}),
    ...(skus ? { p_skus: skus } : {}),
  }, 'ยืนยันไม่สำเร็จ — ฝากโหลดหน้าใหม่แล้วลองอีกครั้ง');
}
export const rejectMaterial = (id, reason = null) =>
  matRpc('reject_material_research', { p_id: id, ...(reason ? { p_reason: reason } : {}) }, 'บันทึกว่าไม่ใช้ไม่สำเร็จ — ฝากโหลดหน้าใหม่แล้วลองอีกครั้ง');
export const applyMaterial = (id, skus) =>
  matRpc('apply_material_research', { p_id: id, p_skus: skus }, 'ใช้ผลนี้กับ SKU เพิ่มไม่สำเร็จ — ฝากโหลดหน้าใหม่แล้วลองอีกครั้ง');
export const cancelQueuedMaterials = () =>
  matRpc('cancel_queued_material_research', {}, 'ยกเลิกคิวไม่สำเร็จ — ฝากลองใหม่อีกครั้ง');

export async function getMaterialControl() {
  const { data, error } = await tbl('material_ai_control').select('*').limit(1).maybeSingle();
  if (error) throw materialsError(error, 'โหลดสถานะคิวไม่สำเร็จ — ฝากลองใหม่อีกครั้ง');
  return data;
}
// ส่งเฉพาะที่จะเปลี่ยน — พัก/เดินคิว ผู้ประสานงานทำได้ · เพดานเงินแอดมินเท่านั้น (เซิร์ฟเวอร์เช็คเอง)
export function setMaterialControl({ paused, dailyMaxUsd, perClickMax } = {}) {
  return matRpc('set_material_ai_control', {
    ...(paused !== undefined ? { p_paused: paused } : {}),
    ...(dailyMaxUsd !== undefined ? { p_daily_max_usd: dailyMaxUsd } : {}),
    ...(perClickMax !== undefined ? { p_per_click_max: perClickMax } : {}),
  }, 'บันทึกการตั้งค่าคิวไม่สำเร็จ — ฝากลองใหม่อีกครั้ง');
}
// ตารางพร้อมไหม — พังแบบไหนก็ตาม = ซ่อนปุ่ม (ไม่มีข้อความเทคนิคให้เห็น · ตารางมาเมื่อไหร่ปุ่มโผล่เอง)
export const materialsReady = () => getMaterialControl().then(() => true, () => false);

// ยอดใช้ 24 ชม. ล่าสุด สำหรับแถบควบคุม — นับแบบเดียวกับด่านเงินในฐานข้อมูล: sum(cost_usd) ที่ started_at อยู่ใน 24 ชม. (deploy-38)
export async function materialSpend24h() {
  const since = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
  const { data, error } = await tbl('material_research').select('cost_usd')
    .gt('started_at', since).limit(5000);
  if (error) throw materialsError(error, 'โหลดยอดใช้ไม่สำเร็จ');
  return (data ?? []).reduce((s, r) => s + (Number(r.cost_usd) || 0), 0);
}

// รายการในตู้/ใบ invoice ที่ใบสินค้ายังไม่มีวัสดุ — ปุ่ม "หาวัสดุที่ยังว่าง" (Console · ใบข้อมูลสินค้า)
//   noCard = บรรทัดที่ยังไม่มีใบสินค้า (ค้นให้ไม่ได้ ต้องสร้างใบก่อน) · filled = ใบที่มีวัสดุแล้ว
export async function materialGapIds(consignmentIds) {
  if (!consignmentIds?.length) return { ids: [], noCard: 0, filled: 0 };
  try {
    const items = await rows(tbl('line_item').select('classification_id').in('consignment_id', consignmentIds));
    const cardIds = [...new Set(items.map(i => i.classification_id).filter(Boolean))];
    const ids = [];
    let filled = 0;
    for (let i = 0; i < cardIds.length; i += 200) {
      const got = await rows(tbl('product_classification').select('id, material').in('id', cardIds.slice(i, i + 200)));
      for (const c of got) { if (String(c.material ?? '').trim()) filled++; else ids.push(c.id); }
    }
    return { ids, noCard: items.filter(i => !i.classification_id).length, filled };
  } catch (e) { throw materialsError(e, 'โหลดรายการสินค้าในใบนี้ไม่สำเร็จ — ฝากลองใหม่อีกครั้ง'); }
}
